import {
    deleteWebWidgetById,
    getWebWidgets,
    getWebWidgetDetailById,
    saveWebWidgetAction,
    saveWebWidgetFileAction,
    deleteWebWidgetFileAction,
    reorderWebWidgetsAction,
} from 'redux/webDesigner/webDesignerActions';
import {
    deleteWidgetById as deleteAppWidgetById,
    getWidgets as getAppWidgets,
    getWidgetDetailById as getAppWidgetDetailById,
    saveWidgetAction as saveAppWidgetAction,
    saveWidgetFileAction as saveAppWidgetFileAction,
    deleteWidgetFileAction as deleteAppWidgetFileAction,
    reorderWidgetsAction as reorderAppWidgetsAction,
} from 'redux/appDesigner/appDesignerActions';
import { validateLink } from 'utils/common';
import { ReactComponent as HorizontalLeftAlign } from 'images/actions/horizontalAlignments/left-align.svg';
import { ReactComponent as HorizontalRightAlign } from 'images/actions/horizontalAlignments/right-align.svg';
import { ReactComponent as Ratio10x16 } from 'images/actions/aspectRatios/ratio-10x16.svg';
import { ReactComponent as Ratio16x16 } from 'images/actions/aspectRatios/ratio-16x16.svg';
import { ReactComponent as Ratio16x12 } from 'images/actions/aspectRatios/ratio-16x12.svg';
import { ReactComponent as Ratio16x10 } from 'images/actions/aspectRatios/ratio-16x10.svg';
import { ReactComponent as Ratio15x7 } from 'images/actions/aspectRatios/ratio-15x7.svg';
import { ReactComponent as Ratio2x3 } from 'images/actions/aspectRatios/ratio-2x3.svg';
import commonStyles from 'styles/main.module.scss';

// Import all module icons
import { ReactComponent as AgendaIcon } from 'images/figma-assets/agenda-icon.svg';
import ContainerIcon from 'images/figma-assets/container-icon.png';
import { ReactComponent as Heading1Icon } from 'images/figma-assets/heading1-icon.svg';
import { ReactComponent as Heading2Icon } from 'images/figma-assets/heading2-icon.svg';
import { ReactComponent as Heading3Icon } from 'images/figma-assets/heading3-icon.svg';
import { ReactComponent as TextIcon } from 'images/figma-assets/text-icon.svg';
import { ReactComponent as AccordionIcon } from 'images/figma-assets/accordion-icon.svg';
import { ReactComponent as ImageIcon } from 'images/figma-assets/image-icon.svg';
import { ReactComponent as VideoIcon } from 'images/figma-assets/video-icon.svg';
import { ReactComponent as AssetIcon } from 'images/figma-assets/asset-icon.svg';
import { ReactComponent as LibraryIcon } from 'images/figma-assets/library-icon.svg';
import { ReactComponent as BannerIcon } from 'images/figma-assets/banner-icon.svg';
import ListIcon from 'images/figma-assets/list-icon.svg';
import SimpleLinkIcon from 'images/figma-assets/simple-link-icon.svg';
import CardLinkIcon from 'images/figma-assets/card-link-icon.svg';
import PosterLinkIcon from 'images/figma-assets/poster-link-icon.svg';
import CarouselIcon from 'images/figma-assets/carousel-icon.svg';
import SlideshoIcon from 'images/figma-assets/slideshow-icon.svg';
import GridIcon from 'images/figma-assets/grid-icon.svg';
import PeopleListIcon from 'images/figma-assets/people-list-icon.png';
import PeopleCarouselIcon from 'images/figma-assets/people-carousel-icon.png';
import PeopleGridIcon from 'images/figma-assets/people-grid-icon.png';
import PeopleFeaturedIcon from 'images/figma-assets/featured-icon.png';
import { ReactComponent as FeaturedAgendaIcon } from 'images/figma-assets/featured-agenda.svg';
import { ReactComponent as PillsIcon } from 'images/figma-assets/pills-icon.svg';
// Legacy module icons for backward compatibility
import { ReactComponent as SurveyIcon } from 'images/ET/survey.svg';
import { ReactComponent as PollIcon } from 'images/ET/poll.svg';
import { ReactComponent as QAIcon } from 'images/ET/qa.svg';
import DirectoryIcon from 'images/figma-assets/link-list-icon.svg';
import storeAndPersistor from 'redux/configureStore';
import {
    BACKGROUND_TYPES,
    colorKeys,
    widgetColorToThemeColorMap,
} from './appDesignerConstants';
import { hexToHSL } from './colorThemeGenerator';
import constants, {
    defaultSelectedPillView,
    defaultSelectedView,
    MODULE_ITEMS,
    ENGAGEMENT_TOOLS_TYPES,
    listOfModulesWhichSupportSmallWidth,
    defaultIsShowFullWidthModule,
    DEFAULT_APP_DESIGNER_COLOR,
    isUrlLikeLinkedPage,
    linkPageItems,
    ENVIRONMENT,
    CUSTOM_FONT_ENVIRONMENTS,
} from './constants';

export const isSecondaryCardConfigurationEnabled = true;
export const isAlertAndWarningColorsEnabled = false;
export const isConfirmationColorsEnabled = false;
export const isLineDividersColorsEnabled = false;
export const isShowBorderWithWidget = false;
export const isEventLevelTypographyEnabled = false;
// TODO: uncomment if we want to allow them to override font family but before that please add logic to load font family if its not in chache
export const isWidgetLevelFontFamilyEnabled = false;
export const isBannerModuleDisabled = false;
export const isPeopleModuleDisabled = true;
// TODO: set this false once video module custom configurations we need to enable
export const isVideoModuleCustomConfigurationDisabled = true;
export const isEnabledImageSelectionWithBG = false;
export const isEnableUseHomeBackgroundColorForAllPages = false;

export const agendaBorderRadius = 8;
export const linkDefaultColor = 'blue';
export const getDefaultIsShowSidebar = (menuPage) =>
    // return menuPage?.settings?.isShowSidebar;
    false;

export const DIRECTORY_LIST_STYLE_OPTIONS = [
    {
        value: 'GRID',
        label: 'Grid',
        icon: 'grid-view',
    },
    {
        value: 'LIST',
        label: 'List',
        icon: 'list-view',
    },
];

// Widget style types configuration
export const WIDGET_STYLE_TYPES = {
    LINKED_PAGE: {
        SIMPLE: { label: 'Simple', value: 'SIMPLE' },
        CARD: { label: 'Card', value: 'CARD' },
        POSTER: { label: 'Poster', value: 'POSTER' }, // Using 'Poster' as the display label
    },
    CONTAINER: {
        LIST_VIEW: { label: 'List', value: 'LIST_VIEW' },
        GRID: { label: 'Grid', value: 'GRID' },
        CAROUSEL: { label: 'Carousel', value: 'CAROUSEL' },
        SLIDESHOW: { label: 'Slideshow', value: 'SLIDESHOW' },
    },
    // Sub-display types for LIST_VIEW
    LIST_VIEW_SUB_TYPES: {
        SIMPLE: { label: 'Simple', value: 'LIST_SIMPLE' },
        CARD: { label: 'Card', value: 'LIST_CARD' },
        POSTER: { label: 'Poster', value: 'LIST_POSTER' },
    },
    // Sub-display types for GRID
    GRID_SUB_TYPES: {
        CARD: { label: 'Card', value: 'GRID_CARD' },
        POSTER: { label: 'Poster', value: 'GRID_POSTER' },
    },
    // Sub-display types for CAROUSEL
    CAROUSEL_SUB_TYPES: {
        SIMPLE: { label: 'Simple', value: 'CAROUSEL_SIMPLE' },
        CARD: { label: 'Card', value: 'CAROUSEL_CARD' },
        POSTER: { label: 'Poster', value: 'CAROUSEL_POSTER' },
    },
    PEOPLE: {
        LIST: { label: 'List', value: 'PEOPLE_LIST' },
        GRID: { label: 'Grid', value: 'PEOPLE_GRID' },
        CAROUSEL: { label: 'Carousel', value: 'PEOPLE_CAROUSEL' },
        FEATURED: { label: 'Featured', value: 'PEOPLE_FEATURED' },
    },
};

const SECTIONS = {
    LAYOUT: 'LAYOUT',
    TEXT: 'TEXT',
    CONTENT: 'CONTENT',
    LINK: 'LINK',
    LINK_COLLECTION: 'LINK COLLECTION',
    AGENDA: 'AGENDA',
    PEOPLE: 'PEOPLE',
};

// API endpoints for engagement tools (used in legacy modules)
const apiEndPointsForEngagementTools = {
    SURVEYS: 'surveys',
    POLLS: 'polls',
    QAS: 'qa',
    QUIZ: 'quizzes',
};

// Legacy modules for backward compatibility - not shown in UI but supported for existing widgets
export const LEGACY_MODULES = [
    {
        id: ENGAGEMENT_TOOLS_TYPES.SURVEY,
        label: 'Survey',
        icon: SurveyIcon,
        data: { apiEndPoint: apiEndPointsForEngagementTools.SURVEYS },
    },
    {
        id: ENGAGEMENT_TOOLS_TYPES.POLL,
        label: 'Poll',
        icon: PollIcon,
        data: { apiEndPoint: apiEndPointsForEngagementTools.POLLS },
    },
    {
        id: ENGAGEMENT_TOOLS_TYPES.QA,
        label: 'Q & A',
        icon: QAIcon,
        data: { apiEndPoint: apiEndPointsForEngagementTools.QAS },
    },
    {
        id: MODULE_ITEMS.DIRECTORY,
        label: 'Directory',
        icon: DirectoryIcon,
        data: {},
    },
];

export const MODULES = {
    // [SECTIONS.LAYOUT]: [
    //     {
    //         id: MODULE_ITEMS.CONTAINER,
    //         label: 'Container',
    //         icon: ContainerIcon,
    //         data: {},
    //     },
    // ],
    [SECTIONS.TEXT]: [
        { id: MODULE_ITEMS.TEXT, label: 'Text', icon: TextIcon, data: {} },
        // TODO: add heading 1, 2, 3
        // {
        //     id: MODULE_ITEMS.TEXT,
        //     label: 'Heading 1',
        //     icon: Heading1Icon,
        //     data: {},
        // },
        // {
        //     id: MODULE_ITEMS.TEXT,
        //     label: 'Heading 2',
        //     icon: Heading2Icon,
        //     data: {},
        // },
        // {
        //     id: MODULE_ITEMS.TEXT,
        //     label: 'Heading 3',
        //     icon: Heading3Icon,
        //     data: {},
        // },
        {
            id: MODULE_ITEMS.ACCORDION,
            label: 'Accordion',
            icon: AccordionIcon,
            data: {},
        },
    ],
    [SECTIONS.CONTENT]: [
        { id: MODULE_ITEMS.IMAGE, label: 'Image', icon: ImageIcon, data: {} },
        { id: MODULE_ITEMS.VIDEO, label: 'Video', icon: VideoIcon, data: {} },
        {
            id: MODULE_ITEMS.DOCUMENT,
            label: 'Asset',
            icon: AssetIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.LIBRARY,
            label: 'Library',
            icon: LibraryIcon,
            data: { isMultiSelectNameOptions: true },
        },
        {
            id: MODULE_ITEMS.BANNER,
            label: 'Banner',
            icon: BannerIcon,
            disabled: isBannerModuleDisabled,
            data: {},
        },
    ],
    [SECTIONS.LINK]: [
        {
            id: MODULE_ITEMS.LINKED_PAGE,
            label: WIDGET_STYLE_TYPES.LINKED_PAGE.SIMPLE.label,
            icon: SimpleLinkIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.LINKED_PAGE,
            label: WIDGET_STYLE_TYPES.LINKED_PAGE.CARD.label,
            icon: CardLinkIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.LINKED_PAGE,
            label: WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.label,
            icon: PosterLinkIcon,
            data: {},
        },
    ],
    [SECTIONS.LINK_COLLECTION]: [
        {
            id: MODULE_ITEMS.CONTAINER,
            label: WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.label,
            icon: ListIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.CONTAINER,
            label: WIDGET_STYLE_TYPES.CONTAINER.GRID.label,
            icon: GridIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.CONTAINER,
            label: WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.label,
            icon: CarouselIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.CONTAINER,
            label: WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.label,
            icon: SlideshoIcon,
            data: {},
        },
    ],
    [SECTIONS.AGENDA]: [
        {
            id: MODULE_ITEMS.MY_AGENDA,
            label: 'Up Next',
            icon: AgendaIcon,
            data: {},
        },
        {
            id: MODULE_ITEMS.FEATURED,
            label: 'Featured',
            icon: FeaturedAgendaIcon,
            disabled: true,
            data: {},
        },
    ],
    [SECTIONS.PEOPLE]: [
        {
            id: MODULE_ITEMS.PEOPLE,
            label: WIDGET_STYLE_TYPES.PEOPLE.FEATURED.label,
            icon: PeopleFeaturedIcon,
            data: { isMultiSelectNameOptions: true },
            disabled: isPeopleModuleDisabled,
        },
        {
            id: MODULE_ITEMS.PEOPLE,
            label: WIDGET_STYLE_TYPES.PEOPLE.LIST.label,
            icon: PeopleListIcon,
            data: { isMultiSelectNameOptions: true },
            disabled: isPeopleModuleDisabled,
        },
        {
            id: MODULE_ITEMS.PEOPLE,
            label: WIDGET_STYLE_TYPES.PEOPLE.GRID.label,
            icon: PeopleGridIcon,
            data: { isMultiSelectNameOptions: true },
            disabled: isPeopleModuleDisabled,
        },
        {
            id: MODULE_ITEMS.PEOPLE,
            label: WIDGET_STYLE_TYPES.PEOPLE.CAROUSEL.label,
            icon: PeopleCarouselIcon,
            data: { isMultiSelectNameOptions: true },
            disabled: isPeopleModuleDisabled,
        },
    ],
};

export const getContainerFieldsDataBasedOnResponse = (
    fieldsData,
    responseData
) => {
    if (responseData?.settings) {
        if (
            fieldsData.useImageAsIcon &&
            fieldsData.displayType.value ===
                WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
            Object.keys(responseData?.settings).includes('useImageAsIcon')
        )
            fieldsData.useImageAsIcon.value =
                responseData?.settings?.useImageAsIcon;

        if (
            fieldsData.pillType &&
            fieldsData.displayType.value ===
                WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value &&
            Object.keys(responseData?.settings).includes('pillType')
        )
            fieldsData.pillType.value = responseData?.settings?.pillType;

        if (
            fieldsData.displayType.value ===
            WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value
        ) {
            if (Object.keys(responseData?.settings).includes('isRotationOn')) {
                fieldsData.isRotationOn.value =
                    responseData?.settings?.isRotationOn;
            }
            if (
                Object.keys(responseData?.settings).includes('rotationInterval')
            ) {
                fieldsData.rotationInterval.value =
                    responseData?.settings?.rotationInterval;
            }
            if (Object.keys(responseData?.settings).includes('isShowTitle')) {
                fieldsData.isShowTitle.value =
                    responseData?.settings?.isShowTitle;
            }
        }

        if (
            fieldsData.isShowChildTitle &&
            Object.keys(responseData?.settings).includes('isShowChildTitle')
        ) {
            fieldsData.isShowChildTitle.value =
                responseData?.settings?.isShowChildTitle;
        }
    }
    return fieldsData;
};

export const getContainerStylesDataBasedOnResponse = (
    stylesData,
    responseData
) => {
    if (
        stylesData?.fontColor?.value &&
        responseData?.settings?.styles?.fontColor
    ) {
        stylesData.fontColor.value = responseData?.settings?.styles?.fontColor;
    }
    if (
        stylesData?.backgroundColor?.value &&
        responseData?.settings?.styles?.backgroundColor
    ) {
        stylesData.backgroundColor.value =
            responseData?.settings?.styles?.backgroundColor;
    }

    // Load sub-display types from settings
    if (
        stylesData?.listViewSubDisplayType &&
        responseData?.settings &&
        Object.keys(responseData?.settings).includes('listViewSubDisplayType')
    ) {
        stylesData.listViewSubDisplayType.value =
            responseData?.settings?.listViewSubDisplayType;
    }
    if (
        stylesData?.gridSubDisplayType &&
        responseData?.settings &&
        Object.keys(responseData?.settings).includes('gridSubDisplayType')
    ) {
        stylesData.gridSubDisplayType.value =
            responseData?.settings?.gridSubDisplayType;
    }
    if (
        stylesData?.carouselSubDisplayType &&
        responseData?.settings &&
        Object.keys(responseData?.settings).includes('carouselSubDisplayType')
    ) {
        stylesData.carouselSubDisplayType.value =
            responseData?.settings?.carouselSubDisplayType;
    }

    return stylesData;
};

export const getContainerRequestData = (requestData, fields, styles) => {
    requestData.widget.settings.displayType = fields.displayType.value;

    // Save sub-display types
    if (
        fields.displayType.value ===
            WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value &&
        styles.listViewSubDisplayType
    ) {
        requestData.widget.settings.listViewSubDisplayType =
            styles.listViewSubDisplayType.value;
    }
    if (
        fields.displayType.value === WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
        styles.gridSubDisplayType
    ) {
        requestData.widget.settings.gridSubDisplayType =
            styles.gridSubDisplayType.value;
    }
    if (
        fields.displayType.value ===
            WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value &&
        styles.carouselSubDisplayType
    ) {
        requestData.widget.settings.carouselSubDisplayType =
            styles.carouselSubDisplayType.value;
    }

    if (
        fields.displayType.value === WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
        fields.useImageAsIcon
    )
        requestData.widget.settings.useImageAsIcon =
            fields.useImageAsIcon.value;

    if (
        fields.displayType.value === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value
    ) {
        // If imageSize is Pill, save pillType
        if (
            fields.pillType &&
            styles?.imageSize?.value === IMAGE_SIZE_VALUES.PILL
        ) {
            requestData.widget.settings.pillType = fields.pillType.value;
        }
    }

    if (
        fields.displayType.value ===
        WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value
    ) {
        requestData.widget.settings.isRotationOn = fields.isRotationOn.value;
        requestData.widget.settings.rotationInterval =
            +fields.rotationInterval.value;
    }

    // NOTE: Style fields like fontColor, backgroundColor, backgroundSelector are now handled
    // by the main save logic in AddEditModule.js which filters based on valid styles for display type.
    // This prevents sending invalid styles (e.g., backgroundColor for CAROUSEL which uses backgroundSelector).

    return requestData;
};

// for web designer will use web endpoints and for app will use app endpoints
export const getWidgetFunctions = (isWebDesigner) =>
    isWebDesigner
        ? {
              getWidgets: getWebWidgets,
              deleteWidgetById: deleteWebWidgetById,
              getWidgetDetailById: getWebWidgetDetailById,
              saveWidgetAction: saveWebWidgetAction,
              saveWidgetFileAction: saveWebWidgetFileAction,
              deleteWidgetFileAction: deleteWebWidgetFileAction,
              reorderWidgetsAction: reorderWebWidgetsAction,
          }
        : {
              getWidgets: getAppWidgets,
              deleteWidgetById: deleteAppWidgetById,
              getWidgetDetailById: getAppWidgetDetailById,
              saveWidgetAction: saveAppWidgetAction,
              saveWidgetFileAction: saveAppWidgetFileAction,
              deleteWidgetFileAction: deleteAppWidgetFileAction,
              reorderWidgetsAction: reorderAppWidgetsAction,
          };

export const labelsForTabItems = {
    DETAILS: 'Details',
    AUDIENCE: 'Audience',
    STYLE: 'Style',
    MODULES: 'Modules',
    LINKS: 'Links',
};

export const SCHEDULING_ALLOWED_MODULES = [
    // uncomment if we want to restrict scheduling for specific modules
    // MODULE_ITEMS.DOCUMENT,
    // MODULE_ITEMS.LINKED_PAGE,
    // ENGAGEMENT_TOOLS_TYPES.SURVEY,
    // ENGAGEMENT_TOOLS_TYPES.QA,
    // ENGAGEMENT_TOOLS_TYPES.QUIZ,
];

export const IMAGE_MODULES = [MODULE_ITEMS.IMAGE, MODULE_ITEMS.BANNER];

export const LINK_TO_FULL_PAGE_MODULES = [MODULE_ITEMS.DIRECTORY];

export const ENGAGEMENT_TOOLS = [
    ENGAGEMENT_TOOLS_TYPES.POLL,
    ENGAGEMENT_TOOLS_TYPES.SURVEY,
    ENGAGEMENT_TOOLS_TYPES.QA,
    ENGAGEMENT_TOOLS_TYPES.QUIZ,
];

export const MATERIAL_RELATED_MODULES = [
    MODULE_ITEMS.DOCUMENT,
    MODULE_ITEMS.LIBRARY,
];

export const TITLE_SELECTION_ALLOWED_MODULES = [
    ...ENGAGEMENT_TOOLS,
    ...MATERIAL_RELATED_MODULES,
    MODULE_ITEMS.DIRECTORY,
    MODULE_ITEMS.PEOPLE,
];

export const BORDER_WIDTH_OPTIONS = [
    { value: 0, label: 'No Border' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
];

// Aspect ratio icon map - maps icon names to their SVG components
const ASPECT_RATIO_ICON_MAP = {
    'ratio-10x16': Ratio10x16,
    'ratio-16x16': Ratio16x16,
    'ratio-16x12': Ratio16x12,
    'ratio-16x10': Ratio16x10,
    'ratio-15x7': Ratio15x7,
    'ratio-2x3': Ratio2x3,
};

export const ASPECT_RATIO_OPTIONS = [
    { value: 'auto', label: 'Auto', icon: 'ratio-10x16' },
    { value: '1:1', label: '1:1', icon: 'ratio-16x16' },
    { value: '4:3', label: '4:3', icon: 'ratio-16x12' },
    { value: '16:9', label: '16:9', icon: 'ratio-16x10' },
    { value: '3:1', label: '3:1', icon: 'ratio-15x7' },
];

export const ASPECT_RATIO_2BY3_OPTION = {
    value: '2:3',
    label: '2:3',
    icon: 'ratio-2x3',
};

export const ASPECT_RATIO_OPTIONS_WITH_2BY3 = [
    ...ASPECT_RATIO_OPTIONS,
    ASPECT_RATIO_2BY3_OPTION,
];

export const ALL_ASPECT_RATIO_OPTIONS = [
    ...ASPECT_RATIO_OPTIONS,
    ASPECT_RATIO_2BY3_OPTION,
];

export const ASPECT_RATIO_OPTIONS_NO_AUTO = ASPECT_RATIO_OPTIONS.filter(
    (opt) => opt.value !== 'auto'
);

/**
 * Get aspect ratio icon component for a given aspect ratio value
 * @param {string} ratio - The aspect ratio value (e.g., '16:9', 'auto')
 * @returns {React.Component} The icon SVG component
 */
export const getAspectRatioIcon = (ratio) => {
    const option = ALL_ASPECT_RATIO_OPTIONS.find((opt) => opt.value === ratio);
    const iconName = option?.icon || 'ratio-16x12';
    return ASPECT_RATIO_ICON_MAP[iconName] || Ratio16x12;
};

/**
 * Normalizes aspect ratio value based on available options
 * If current value is not in available options, returns default '1:1'
 * @param {string} currentValue - Current aspect ratio value
 * @param {Array} availableOptions - Array of available aspect ratio options
 * @returns {string} Normalized aspect ratio value
 */
export const normalizeAspectRatioValue = (currentValue, availableOptions) => {
    if (!currentValue || !availableOptions) return '1:1';
    const isAvailable = availableOptions.some(
        (opt) => opt.value === currentValue
    );
    return isAvailable ? currentValue : '1:1';
};

export const getAspectRatioOptionsForDisplayType = (
    moduleName,
    displayType
) => {
    if (moduleName !== MODULE_ITEMS.CONTAINER) return ASPECT_RATIO_OPTIONS;

    const noAutoTypes = [
        // Do uncomment if we want to hide auto for below display types
        // WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value,
        // WIDGET_STYLE_TYPES.CONTAINER.GRID.value,
        // WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.CARD.value,
        // WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.POSTER.value,
        // WIDGET_STYLE_TYPES.GRID_SUB_TYPES.CARD.value,
        WIDGET_STYLE_TYPES.GRID_SUB_TYPES.POSTER.value,
        WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value,
        WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.CARD.value,
        WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.POSTER.value,
        WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value,
    ];

    const isDoNotReturnAutoAspectRatio = noAutoTypes.includes(displayType);

    const posterTypes = [
        WIDGET_STYLE_TYPES.GRID_SUB_TYPES.POSTER.value,
        WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.POSTER.value,
    ];

    const isPoster = posterTypes.includes(displayType);

    if (isPoster) {
        return ASPECT_RATIO_OPTIONS_WITH_2BY3.filter((opt) =>
            isDoNotReturnAutoAspectRatio ? opt.value !== 'auto' : true
        );
    }

    if (isDoNotReturnAutoAspectRatio) {
        return ASPECT_RATIO_OPTIONS_NO_AUTO;
    }
    return ASPECT_RATIO_OPTIONS;
};

export const FILL_MODE_OPTIONS = [
    { value: 'fill', label: 'Fill', width: '80px' },
    { value: 'fit', label: 'Fit', width: '80px' },
];

export const LIST_STYLE_OPTIONS = [
    { value: 'LIST_SIMPLE', label: 'Simple', width: '80px' },
    { value: 'LIST_CARD', label: 'Card', width: '80px' },
    { value: 'LIST_POSTER', label: 'Poster', width: '80px' },
];

export const GRID_STYLE_OPTIONS = [
    { value: 'GRID_CARD', label: 'Card', width: '80px' },
    { value: 'GRID_POSTER', label: 'Poster', width: '80px' },
];

export const MIN_GRID_COLUMNS_OPTIONS = [
    { value: 2, label: '2', width: '50px' },
    { value: 3, label: '3', width: '50px' },
];

export const CAROUSEL_STYLE_OPTIONS = [
    { value: 'CAROUSEL_SIMPLE', label: 'Simple', width: '80px' },
    { value: 'CAROUSEL_CARD', label: 'Card', width: '80px' },
    { value: 'CAROUSEL_POSTER', label: 'Poster', width: '80px' },
];

export const SORTED_BY_OPTIONS = [
    { value: 'firstName', label: 'First name' },
    { value: 'lastName', label: 'Last name' },
];

// Image size constants - centralized values used across the app
export const IMAGE_SIZE_VALUES = {
    SMALL: 'S',
    MEDIUM: 'M',
    LARGE: 'L',
    PILL: 'PILL',
};

export const imageSizeIconSizes = {
    [IMAGE_SIZE_VALUES.SMALL]: '32px',
    [IMAGE_SIZE_VALUES.MEDIUM]: '128px',
    [IMAGE_SIZE_VALUES.LARGE]: '192px',
};

export const defaultFillMode = 'fit';

export const imageContainerSizes = {
    [IMAGE_SIZE_VALUES.SMALL]: '96px',
    [IMAGE_SIZE_VALUES.MEDIUM]: '128px',
    [IMAGE_SIZE_VALUES.LARGE]: '144px',
};

export const imageSizeImgSizes = {
    [IMAGE_SIZE_VALUES.SMALL]: '32px',
    [IMAGE_SIZE_VALUES.MEDIUM]: '44px',
    [IMAGE_SIZE_VALUES.LARGE]: '56px',
};

export const baseImageOptions = [
    { value: IMAGE_SIZE_VALUES.SMALL, label: 'S', width: '40px' },
    { value: IMAGE_SIZE_VALUES.MEDIUM, label: 'M', width: '40px' },
];

// Get imageSize options based on display type
// Only S and M are supported for all widgets
export const getImageSizeOptions = (moduleName, displayType) =>
    baseImageOptions;

export const isShowImageField = (moduleName, selectedDisplayType) =>
    IMAGE_MODULES.includes(moduleName) ||
    (moduleName === MODULE_ITEMS.LINKED_PAGE &&
        [
            WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.value,
            WIDGET_STYLE_TYPES.LINKED_PAGE.CARD.value,
        ].includes(selectedDisplayType));

// Check if a module should show the button/link field (for Banner widget)
export const isShowButtonField = (moduleName) =>
    moduleName === MODULE_ITEMS.BANNER;

const isShowDescriptionField = (moduleName, selectedDisplayType) =>
    [MODULE_ITEMS.VIDEO, MODULE_ITEMS.BANNER].includes(moduleName) ||
    (moduleName === MODULE_ITEMS.PEOPLE &&
        [WIDGET_STYLE_TYPES.PEOPLE.FEATURED.value].includes(
            selectedDisplayType
        )) ||
    (moduleName === MODULE_ITEMS.LINKED_PAGE &&
        [
            WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.value,
            WIDGET_STYLE_TYPES.LINKED_PAGE.CARD.value,
        ].includes(selectedDisplayType)) ||
    (moduleName === MODULE_ITEMS.CONTAINER &&
        selectedDisplayType &&
        selectedDisplayType !== WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value &&
        selectedDisplayType !== WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
        selectedDisplayType !== WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value &&
        selectedDisplayType !== WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value);

export const getVisibleFields = ({
    selectedModule,
    isMenuPage,
    isWebDesigner,
    isConfigurable,
    isSubModuleInsideContainerWidget,
    activePage,
    selectedDisplayType,
}) => {
    const moduleName = selectedModule?.name;

    // For menu page linked page widgets, always show image field (as icon)
    const isMenuPageLinkedPage =
        isMenuPage && moduleName === MODULE_ITEMS.LINKED_PAGE;

    const fields = {
        // Common fields - always visible
        name: true,
        description: isShowDescriptionField(
            selectedModule?.name,
            selectedDisplayType
        ),
        // Conditional fields
        // comment or uncomment to show hide seprator line
        isShowSeparatorLine: false,
        // !isMenuPage &&
        // !isWebDesigner &&
        // (!isSubModuleInsideContainerWidget ||
        //     (isSubModuleInsideContainerWidget &&
        //         activePage.displayType ===
        //             WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value)),
        directory: moduleName === MODULE_ITEMS.DIRECTORY,
        linkedPage: [MODULE_ITEMS.LINKED_PAGE, MODULE_ITEMS.BANNER].includes(
            moduleName
        ),
        isVisible:
            moduleName === MODULE_ITEMS.LINKED_PAGE &&
            isConfigurable &&
            !isSubModuleInsideContainerWidget,
        text:
            moduleName === MODULE_ITEMS.TEXT ||
            moduleName === MODULE_ITEMS.ACCORDION,
        isExpandByDefault: moduleName === MODULE_ITEMS.ACCORDION,
        image:
            isMenuPageLinkedPage ||
            isShowImageField(moduleName, selectedDisplayType),
        isOpenImageOnClick: moduleName === MODULE_ITEMS.IMAGE,
        isShowFullWidthBanner:
            isBannerModuleDisabled && moduleName === MODULE_ITEMS.IMAGE,
        video: moduleName === MODULE_ITEMS.VIDEO,

        // Banner widget fields
        includeButton: moduleName === MODULE_ITEMS.BANNER,
        buttonLabel: moduleName === MODULE_ITEMS.BANNER,

        showControls: false,
        autoplay:
            !isVideoModuleCustomConfigurationDisabled &&
            moduleName === MODULE_ITEMS.VIDEO,
        muteAudio:
            !isVideoModuleCustomConfigurationDisabled &&
            moduleName === MODULE_ITEMS.VIDEO,
        linkToFullPageList: LINK_TO_FULL_PAGE_MODULES.includes(moduleName),
        isShowFullWidthModule:
            listOfModulesWhichSupportSmallWidth.includes(moduleName),

        // Container widget fields
        displayType:
            moduleName === MODULE_ITEMS.CONTAINER ||
            moduleName === MODULE_ITEMS.LINKED_PAGE ||
            moduleName === MODULE_ITEMS.PEOPLE,
        isRotationOn: moduleName === MODULE_ITEMS.CONTAINER,
        rotationInterval: moduleName === MODULE_ITEMS.CONTAINER,
        isShowTitle:
            moduleName === MODULE_ITEMS.CONTAINER ||
            moduleName === MODULE_ITEMS.BANNER ||
            (moduleName === MODULE_ITEMS.LINKED_PAGE &&
                selectedDisplayType ===
                    WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.value),
        isShowChildTitle: moduleName === MODULE_ITEMS.CONTAINER,
        pillType: false,
        // pillType: moduleName === MODULE_ITEMS.CONTAINER,
        // useImageAsIcon: moduleName === MODULE_ITEMS.CONTAINER,

        // People widget fields
        selectedUsers: moduleName === MODULE_ITEMS.PEOPLE,
        peopleSubtitleFields:
            moduleName === MODULE_ITEMS.PEOPLE &&
            selectedDisplayType !== WIDGET_STYLE_TYPES.PEOPLE.FEATURED.value,
        peopleProfile: moduleName === MODULE_ITEMS.PEOPLE,
        sortedBy: false, // update condition if any widget need sorted by
    };

    return fields;
};

export const getVisibleStyles = (
    selectedModule,
    selectedDisplayType = null,
    isMenuPage = false,
    listViewSubDisplayType = null,
    gridSubDisplayType = null,
    carouselSubDisplayType = null
) => {
    const moduleName = selectedModule?.name;
    const isLinkedPageModule = moduleName === MODULE_ITEMS.LINKED_PAGE;
    const isContainerWidget = moduleName === MODULE_ITEMS.CONTAINER;
    const isPeopleWidget = moduleName === MODULE_ITEMS.PEOPLE;
    const isEngagementToolWidget = ENGAGEMENT_TOOLS.includes(moduleName);

    const isShowAspectRatio = [
        MODULE_ITEMS.IMAGE,
        MODULE_ITEMS.BANNER,
    ].includes(moduleName);
    const isShowAlignment = [MODULE_ITEMS.IMAGE].includes(moduleName);
    const isShowFontColor = [
        MODULE_ITEMS.TEXT,
        MODULE_ITEMS.ACCORDION,
        MODULE_ITEMS.VIDEO,
        MODULE_ITEMS.DOCUMENT,
        MODULE_ITEMS.LIBRARY,
        MODULE_ITEMS.BANNER,
        ENGAGEMENT_TOOLS_TYPES.SURVEY,
        ENGAGEMENT_TOOLS_TYPES.POLL,
        ENGAGEMENT_TOOLS_TYPES.QA,
    ].includes(moduleName);
    const isShowBackground = [
        MODULE_ITEMS.TEXT,
        MODULE_ITEMS.ACCORDION,
        MODULE_ITEMS.DOCUMENT,
        MODULE_ITEMS.LIBRARY,
        ENGAGEMENT_TOOLS_TYPES.SURVEY,
        ENGAGEMENT_TOOLS_TYPES.POLL,
        ENGAGEMENT_TOOLS_TYPES.QA,
    ].includes(moduleName);
    const isShowLabelFont = [
        MODULE_ITEMS.DOCUMENT,
        MODULE_ITEMS.LIBRARY,
        ENGAGEMENT_TOOLS_TYPES.SURVEY,
        ENGAGEMENT_TOOLS_TYPES.POLL,
        ENGAGEMENT_TOOLS_TYPES.QA,
    ].includes(moduleName);
    const isShowSubtitleFont = [
        MODULE_ITEMS.DOCUMENT,
        MODULE_ITEMS.LIBRARY,
    ].includes(moduleName);
    const isShowUsePageLevelFonts = [
        MODULE_ITEMS.DOCUMENT,
        MODULE_ITEMS.LINKED_PAGE,
        MODULE_ITEMS.CONTAINER,
    ].includes(moduleName);
    const isShowBackgroundSelector = false;
    const isShowFillMode = IMAGE_MODULES.includes(moduleName);
    const isShowButtonStyles = moduleName === MODULE_ITEMS.BANNER;
    const isShowAccentColor = [
        MODULE_ITEMS.ACCORDION,
        MODULE_ITEMS.BANNER,
        MODULE_ITEMS.DOCUMENT,
        MODULE_ITEMS.LIBRARY,
        ENGAGEMENT_TOOLS_TYPES.SURVEY,
        ENGAGEMENT_TOOLS_TYPES.POLL,
        ENGAGEMENT_TOOLS_TYPES.QA,
    ].includes(moduleName);
    const isShowTextAndIconOnAccentColor = [
        MODULE_ITEMS.BANNER,
        ENGAGEMENT_TOOLS_TYPES.SURVEY,
        ENGAGEMENT_TOOLS_TYPES.POLL,
        ENGAGEMENT_TOOLS_TYPES.QA,
    ].includes(moduleName);
    const isShowSecondaryCardColors =
        isSecondaryCardConfigurationEnabled && [].includes(moduleName);
    let styles = {
        // Border fields
        // borderColor: isContainerWidget,
        // borderWidth: isContainerWidget,

        // Sub-display type selectors
        listViewSubDisplayType:
            isContainerWidget &&
            selectedDisplayType ===
                WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value,
        gridSubDisplayType:
            isContainerWidget &&
            selectedDisplayType === WIDGET_STYLE_TYPES.CONTAINER.GRID.value,
        minGridColumns: false,
        carouselSubDisplayType:
            isContainerWidget &&
            selectedDisplayType === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value,

        // Image style fields
        aspectRatio: isShowAspectRatio,
        fillMode: isShowFillMode,
        horizontalAlignment: isShowAlignment,

        // Text style fields
        fontColor: isShowFontColor,
        backgroundColor: isShowBackground,
        backgroundSelector: isShowBackgroundSelector,

        labelFont: isShowLabelFont,
        subtitleFont: isShowSubtitleFont,

        // accent and accent icon or text color
        accentColor: isShowAccentColor,
        textAndIconOnAccentColor: isShowTextAndIconOnAccentColor,

        // Secondary card colors - for Container and Library widgets
        secondaryCardsPanelsColor: isShowSecondaryCardColors,
        textAndIconOnSecondaryCardsPanelsColor: isShowSecondaryCardColors,
    };

    // Add display type-specific styles for LINKED_PAGE, CONTAINER, and PEOPLE modules
    if (
        (isLinkedPageModule || isContainerWidget || isPeopleWidget) &&
        selectedDisplayType
    ) {
        let effectiveDisplayType = selectedDisplayType;
        if (isContainerWidget) {
            if (
                selectedDisplayType ===
                    WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value &&
                listViewSubDisplayType
            ) {
                effectiveDisplayType = listViewSubDisplayType;
            } else if (
                selectedDisplayType ===
                    WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
                gridSubDisplayType
            ) {
                effectiveDisplayType = gridSubDisplayType;
            } else if (
                selectedDisplayType ===
                    WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value &&
                carouselSubDisplayType
            ) {
                effectiveDisplayType = carouselSubDisplayType;
            }
        }

        const displayTypeSpecificStyles = getDisplayTypeSpecificStyles(
            moduleName,
            effectiveDisplayType,
            isMenuPage
        );
        styles = { ...styles, ...displayTypeSpecificStyles };
    }
    return styles;
};

export const defaultWidgetBackgroundColor = '#ffffff';
export const defaultWidgetFontColor = '#000000';
export const defaultWidgetTextAndIconOnBackgroundColor = '#000000';

/**
 * Get background color based on backgroundSelector type
 * @param {Object} backgroundSelector - The background selector object with backgroundType and backgroundColor
 * @param {string} fallbackColor - Fallback color if no background is set
 * @returns {string} The background color value
 */
export const getBackgroundColorFromSelector = (
    backgroundSelector,
    fallbackColor = 'transparent'
) => {
    if (!backgroundSelector) return fallbackColor;

    const { backgroundType, backgroundColor } = backgroundSelector;

    if (backgroundType === BACKGROUND_TYPES.NONE) {
        return 'transparent';
    }
    if (backgroundType === BACKGROUND_TYPES.COLOR) {
        return backgroundColor || fallbackColor;
    }
    // For Gradient type, return transparent as gradient is handled by backgroundImage
    return 'transparent';
};

/**
 * Get background image (gradient) based on backgroundSelector type
 * @param {Object} backgroundSelector - The background selector object with backgroundType and backgroundGradient
 * @returns {string|undefined} The background gradient value or undefined
 */
export const getBackgroundImageFromSelector = (backgroundSelector) => {
    if (!backgroundSelector) return undefined;

    const { backgroundType, backgroundGradient } = backgroundSelector;

    if (backgroundType === BACKGROUND_TYPES.GRADIENT) {
        return backgroundGradient;
    }
    return undefined;
};

/**
 * Get background styles object for inline styles
 * @param {Object} backgroundSelector - The background selector object
 * @param {string} fallbackColor - Fallback color if no background is set
 * @returns {Object} Object with background or backgroundColor property
 */
export const getBackgroundStylesFromSelector = (
    backgroundSelector,
    fallbackColor = 'transparent'
) => {
    if (!backgroundSelector) {
        return { backgroundColor: fallbackColor };
    }

    const { backgroundType, backgroundColor, backgroundGradient } =
        backgroundSelector;

    if (backgroundType === BACKGROUND_TYPES.NONE) {
        return { backgroundColor: 'transparent' };
    }
    if (backgroundType === BACKGROUND_TYPES.GRADIENT && backgroundGradient) {
        return { backgroundColor: backgroundGradient };
    }
    if (backgroundType === BACKGROUND_TYPES.COLOR && backgroundColor) {
        return { backgroundColor };
    }

    return { backgroundColor: fallbackColor };
};

export const defaultFontFamily = 'Inter';

// Top 20 most popular web fonts for cross-platform compatibility
// Organized by category: Sans-serif, Serif, Display, Monospace
export const fontFamilyOptions = [
    // =========================
    // Sans-serif (10)
    // =========================
    {
        value: 'Inter',
        label: 'Inter',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Roboto',
        label: 'Roboto',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Open Sans',
        label: 'Open Sans',
        category: 'sans-serif',
        googleFont: true,
    },
    { value: 'Lato', label: 'Lato', category: 'sans-serif', googleFont: true },
    {
        value: 'Montserrat',
        label: 'Montserrat',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Poppins',
        label: 'Poppins',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Nunito',
        label: 'Nunito',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Work Sans',
        label: 'Work Sans',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Rubik',
        label: 'Rubik',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'Outfit',
        label: 'Outfit',
        category: 'sans-serif',
        googleFont: true,
    },
    {
        value: 'McKinsey Sans',
        label: 'McKinsey Sans',
        category: 'sans-serif',
        googleFont: false,
        customFont: true,
        customFontCss: '/fonts/mckinsey-sans/mckinsey-sans.css',
        environments: CUSTOM_FONT_ENVIRONMENTS,
        weights: [300, 400, 500],
    },

    // =========================
    // Serif (10)
    // =========================
    {
        value: 'Playfair Display',
        label: 'Playfair Display',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Source Serif 4',
        label: 'Source Serif 4',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'IBM Plex Serif',
        label: 'IBM Plex Serif',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Crimson Pro',
        label: 'Crimson Pro',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Spectral',
        label: 'Spectral',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Noto Serif Display',
        label: 'Noto Serif Display',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Libre Baskerville',
        label: 'Libre Baskerville',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'PT Serif Caption',
        label: 'PT Serif Caption',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Merriweather Sans',
        label: 'Merriweather Sans',
        category: 'serif',
        googleFont: true,
    },
    {
        value: 'Alegreya',
        label: 'Alegreya',
        category: 'serif',
        googleFont: true,
    },
];

/**
 * Returns font family options available in the current environment.
 * Custom/proprietary fonts are filtered by their `environments` allowlist.
 */
export const getFontFamilyOptions = () =>
    fontFamilyOptions
        .filter(
            (f) => !f.environments || f.environments.includes(ENVIRONMENT)
        )
        .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Maps a requested font weight to the closest weight available for the font family.
 * McKinsey Sans only ships 300/400/500 — 600/700 map to 500.
 */
export const resolveFontWeight = (fontFamily, fontWeight) => {
    const option = fontFamilyOptions.find((f) => f.value === fontFamily);
    if (!option?.weights) return fontWeight;

    const w = Number(fontWeight);
    if (option.weights.includes(w)) return w;
    if (w >= 600) return 500;
    if (w === 300 && !option.weights.includes(300)) return 400;

    return option.weights.reduce((closest, weight) =>
        Math.abs(weight - w) < Math.abs(closest - w) ? weight : closest
    );
};

// Font weights to load for each font (400=Regular, 500=Medium, 600=Semibold, 700=Bold)
export const FONT_WEIGHTS_TO_LOAD = [400, 500, 600, 700];

// Cache for loaded fonts to prevent duplicate loading
const loadedFonts = new Set();

const loadGoogleFontStylesheet = (fontFamily, fontOption) =>
    new Promise((resolve, reject) => {
        try {
            const fontWeights = FONT_WEIGHTS_TO_LOAD.join(';');
            const fontFamilyEncoded = fontFamily.replace(/ /g, '+');

            const fontUrl = `https://fonts.googleapis.com/css2?family=${fontFamilyEncoded}:wght@${fontWeights.replace(
                /;/g,
                ';'
            )}&display=swap`;

            const existingLink = document.querySelector(
                `link[href*="${fontFamilyEncoded}"]`
            );
            if (existingLink) {
                loadedFonts.add(fontFamily);
                resolve(true);
                return;
            }

            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = fontUrl;

            link.onload = () => {
                loadedFonts.add(fontFamily);
                resolve(true);
            };

            link.onerror = () => {
                reject(new Error(`Failed to load font: ${fontFamily}`));
            };

            document.head.appendChild(link);
        } catch (error) {
            reject(error);
        }
    });

const loadCustomFontStylesheet = (fontFamily, fontOption) =>
    new Promise((resolve, reject) => {
        const cssUrl = fontOption.customFontCss;
        if (!cssUrl) {
            resolve(true);
            return;
        }

        const existingLink = document.querySelector(`link[href="${cssUrl}"]`);
        if (existingLink) {
            loadedFonts.add(fontFamily);
            resolve(true);
            return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;

        link.onload = () => {
            loadedFonts.add(fontFamily);
            resolve(true);
        };

        link.onerror = () => {
            reject(new Error(`Failed to load custom font: ${fontFamily}`));
        };

        document.head.appendChild(link);
    });

/**
 * Dynamically loads an event font (Google Font or self-hosted custom font).
 * Only loads fonts that are actually used; prevents duplicate loading via cache.
 *
 * @param {string} fontFamily - The font family name to load
 * @returns {Promise<boolean>} - Returns true if loaded successfully
 */
export const loadEventFont = (fontFamily) => {
    const fontOption = fontFamilyOptions.find((f) => f.value === fontFamily);

    if (!fontOption || loadedFonts.has(fontFamily)) {
        return Promise.resolve(true);
    }

    if (fontOption.customFont) {
        if (
            fontOption.environments &&
            !fontOption.environments.includes(ENVIRONMENT)
        ) {
            return Promise.resolve(true);
        }
        return loadCustomFontStylesheet(fontFamily, fontOption);
    }

    if (fontOption.googleFont) {
        return loadGoogleFontStylesheet(fontFamily, fontOption);
    }

    return Promise.resolve(true);
};

/**
 * @deprecated Use loadEventFont — kept for backward compatibility
 */
export const loadGoogleFont = (fontFamily) => loadEventFont(fontFamily);

/**
 * Preloads essential fonts used in the application
 * Only loads Inter (default) on app initialization
 * Other fonts load on-demand when selected
 */
export const preloadEssentialFonts = () => {
    loadEventFont('Inter').catch(() => {
        // Failed to preload Inter font - will fallback to system fonts
    });
};

/**
 * Gets the font family with fallback
 * @param {string} fontFamily - The primary font family
 * @returns {string} - Font family with appropriate fallback
 */
export const getFontFamilyWithFallback = (fontFamily) => {
    const fontOption = fontFamilyOptions.find((f) => f.value === fontFamily);
    if (!fontOption) return fontFamily;

    const fallbacks = {
        'sans-serif':
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        serif: 'Georgia, "Times New Roman", serif',
        display: 'Impact, "Arial Black", sans-serif',
        monospace: '"Courier New", Courier, monospace',
    };

    return `"${fontFamily}", ${fallbacks[fontOption.category] || 'sans-serif'}`;
};
export const fontSizeOptions = [
    { value: 10, label: '10' },
    { value: 12, label: '12' },
    { value: 14, label: '14' },
    { value: 16, label: '16' },
    { value: 18, label: '18' },
    { value: 20, label: '20' },
    { value: 22, label: '22' },
    { value: 24, label: '24' },
    { value: 26, label: '26' },
    { value: 28, label: '28' },
    { value: 30, label: '30' },
    { value: 32, label: '32' },
    { value: 34, label: '34' },
];

export const fontWeightsMap = {
    Regular: 400,
    Medium: 500,
    Semibold: 600,
    Bold: 700,
};

export const fontWeightOptions = [
    { value: fontWeightsMap.Regular, label: 'Regular' },
    { value: fontWeightsMap.Medium, label: 'Medium' },
    { value: fontWeightsMap.Semibold, label: 'Semibold' },
    { value: fontWeightsMap.Bold, label: 'Bold' },
];

export const defaultWidgetLabelFont = {
    fontFamily: defaultFontFamily,
    fontSize: 16,
    fontWeight: fontWeightsMap.Medium,
};

export const defaultWidgetSubtitleFont = {
    fontFamily: defaultFontFamily,
    fontSize: 14,
    fontWeight: fontWeightsMap.Regular,
};

export const getInitialValues = ({
    selectedModule,
    isMenuPage,
    isLinkedPageRequired,
    isWebDesigner,
    isConfigurable,
    isSubModuleInsideContainerWidget,
    activePage,
}) => {
    let labelFontText = 'Title';
    let subtitleFontText = 'Subtitle';
    if (
        [MODULE_ITEMS.LIBRARY, MODULE_ITEMS.DOCUMENT].includes(
            selectedModule?.name
        )
    ) {
        labelFontText = 'Asset Title';
        subtitleFontText = 'Document Type';
    }

    let labelForTextAndIconOnAccentColor = 'Text and Icon on Accent Color';
    if (selectedModule?.name === MODULE_ITEMS.BANNER) {
        labelForTextAndIconOnAccentColor = 'Text Color on Button';
    } else if (
        selectedModule?.name === MODULE_ITEMS.LINKED_PAGE ||
        selectedModule?.name === MODULE_ITEMS.CONTAINER
    ) {
        labelForTextAndIconOnAccentColor = 'Icon Color';
    } else if (
        [
            ENGAGEMENT_TOOLS_TYPES.SURVEY,
            ENGAGEMENT_TOOLS_TYPES.POLL,
            ENGAGEMENT_TOOLS_TYPES.QA,
        ].includes(selectedModule?.name)
    ) {
        labelForTextAndIconOnAccentColor = 'Icon Color';
    }
    const initialValues = {
        // Common fields
        name: {
            id: 'name',
            name: 'name',
            label: !isMenuPage ? 'Title' : 'Link Title',
            isRequired: true, // Title is required for all modules
            value: '',
            errorMessage: '',
            placeholder: '',
        },
        description: {
            id: 'description',
            name: 'description',
            label: 'Subtitle',
            isRequired: false,
            value: '',
            errorMessage: '',
            placeholder: 'Subtitle',
            variant:
                selectedModule?.name === MODULE_ITEMS.LINKED_PAGE
                    ? 'horizontal-stacked'
                    : 'horizontal',
        },
        isShowSeparatorLine: {
            id: 'isShowSeparatorLine',
            name: 'isShowSeparatorLine',
            label: 'Add separator',
            isRequired: true,
            value: true,
            errorMessage: '',
            placeholder: '',
        },

        // Directory specific fields
        directory: {
            id: 'directory',
            name: 'directory',
            label: 'Directory',
            isRequired: false,
            value: [],
            errorMessage: '',
            placeholder: '',
        },

        // Linked page specific fields
        linkedPage: {
            id: 'linkedPage',
            name: 'linkedPage',
            label: !isMenuPage ? 'Type' : 'Target',
            isRequired: isLinkedPageRequired,
            value: '',
            errorMessage: '',
            placeholder: '',
        },
        isVisible: {
            id: 'isVisible',
            name: 'isVisible',
            label: 'Is Visible?',
            isRequired: true,
            value: false,
            errorMessage: '',
            placeholder: '',
        },

        // Text widget specific fields
        text: {
            id: 'text',
            name: 'text',
            label: 'Text',
            isRequired: true,
            value: selectedModule?.defaultSettings?.defaultText || '',
            defaultValue: selectedModule?.defaultSettings?.defaultText || '',
            errorMessage: '',
            placeholder: '',
        },
        isExpandByDefault: {
            id: 'isExpandByDefault',
            name: 'isExpandByDefault',
            label: 'Expand by default',
            isRequired: false,
            value: false,
            errorMessage: '',
            placeholder: '',
        },

        // Image specific fields - always include, let UI decide visibility
        image: {
            id: 'image',
            name: 'image',
            label: !isMenuPage ? 'Image' : 'Link Icon',
            isRequired:
                !isMenuPage &&
                isShowImageField(
                    selectedModule?.name,
                    selectedModule?.defaultSettings?.displayType ||
                        getDefaultWidgetDisplayType(selectedModule?.name)
                ),
            value: '',
            errorMessage: '',
            placeholder: '',
        },
        isOpenImageOnClick: {
            id: 'isOpenImageOnClick',
            name: 'isOpenImageOnClick',
            label: 'Open image on click',
            isRequired: false,
            value: false,
            errorMessage: '',
            placeholder: '',
        },
        isShowFullWidthBanner: {
            id: 'isShowFullWidthBanner',
            name: 'isShowFullWidthBanner',
            label: 'Is Show Full Width Banner?',
            isRequired: false,
            value: false,
            errorMessage: '',
            placeholder: '',
        },

        // Video specific fields
        video: {
            id: 'video',
            name: 'video',
            label: 'Video',
            isRequired: true,
            value: '',
            errorMessage: '',
            placeholder: 'Enter url here',
        },
        showControls: {
            id: 'showControls',
            name: 'showControls',
            label: 'Show controls',
            isRequired: false,
            value: true,
            errorMessage: '',
            placeholder: '',
        },
        autoplay: {
            id: 'autoplay',
            name: 'autoplay',
            label: 'Autoplay',
            isRequired: false,
            value: false,
            errorMessage: '',
            placeholder: '',
        },
        muteAudio: {
            id: 'muteAudio',
            name: 'muteAudio',
            label: 'Mute audio by default',
            isRequired: false,
            value: false,
            errorMessage: '',
            placeholder: '',
        },

        // Link to full page specific fields
        linkToFullPageList: {
            id: 'linkToFullPageList',
            name: 'linkToFullPageList',
            label: 'Link to full page list',
            isRequired: true,
            value: false,
            errorMessage: '',
            placeholder: '',
        },

        displayType: {
            id: 'displayType',
            name: 'displayType',
            label: 'Display Type',
            isRequired: false,
            value:
                selectedModule?.defaultSettings?.displayType ||
                getDefaultWidgetDisplayType(selectedModule?.name) ||
                WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value,
            errorMessage: '',
            placeholder: '',
        },
        // Container widget specific fields
        isRotationOn: {
            id: 'isRotationOn',
            name: 'isRotationOn',
            label: 'Auto-advance slides',
            isRequired: false,
            value: selectedModule?.defaultSettings?.isRotationOn ?? true,
            errorMessage: '',
            placeholder: '',
        },
        rotationInterval: {
            id: 'rotationInterval',
            name: 'rotationInterval',
            label: 'Rotation interval',
            isRequired: true,
            value: selectedModule?.defaultSettings?.rotationInterval ?? 5,
            errorMessage: '',
            placeholder: '',
        },
        isShowTitle: {
            id: 'isShowTitle',
            name: 'isShowTitle',
            label: 'Is Show Title?',
            isRequired: false,
            value: selectedModule?.defaultSettings?.isShowTitle ?? true,
            errorMessage: '',
            placeholder: '',
        },
        isShowChildTitle: {
            id: 'isShowChildTitle',
            name: 'isShowChildTitle',
            label: 'Is Show Child Title?',
            isRequired: false,
            value: selectedModule?.defaultSettings?.isShowChildTitle ?? true,
            errorMessage: '',
            placeholder: '',
        },
        pillType: {
            id: 'pillType',
            name: 'pillType',
            label: 'Pill Type',
            isRequired: false,
            value:
                selectedModule?.defaultSettings?.pillType ||
                defaultSelectedPillView,
            errorMessage: '',
            placeholder: '',
        },
        useImageAsIcon: {
            id: 'useImageAsIcon',
            name: 'useImageAsIcon',
            label: 'Use Image As Icon',
            isRequired: false,
            value: true,
            errorMessage: '',
            placeholder: '',
        },

        // Web specific fields
        isShowFullWidthModule: {
            id: 'isShowFullWidthModule',
            name: 'isShowFullWidthModule',
            label: 'Show Full Width Module On Web',
            isRequired: false,
            value: defaultIsShowFullWidthModule,
            errorMessage: '',
            placeholder: '',
        },

        // People widget specific fields
        peopleSubtitleFields: {
            id: 'peopleSubtitleFields',
            name: 'peopleSubtitleFields',
            label: 'Subtitle Fields',
            isRequired: false,
            value: [],
            errorMessage: '',
            placeholder: '',
        },
        peopleProfile: {
            id: 'peopleProfile',
            name: 'peopleProfile',
            label: 'Profile',
            isRequired: true,
            value: '',
            errorMessage: '',
            placeholder: '',
        },
        sortedBy: {
            id: 'sortedBy',
            name: 'sortedBy',
            label: 'Sorted By',
            isRequired: true,
            value: 'lastName',
            errorMessage: '',
            placeholder: '',
        },

        // Banner widget specific fields
        includeButton: {
            id: 'includeButton',
            name: 'includeButton',
            label: 'Include a button',
            isRequired: false,
            value: false,
            errorMessage: '',
            placeholder: '',
        },
        buttonLabel: {
            id: 'buttonLabel',
            name: 'buttonLabel',
            label: 'Label',
            isRequired: false,
            value: '',
            errorMessage: '',
            placeholder: 'Enter button text',
        },
    };
    const initialValuesForStyle = {
        listViewSubDisplayType: {
            id: 'listViewSubDisplayType',
            name: 'listViewSubDisplayType',
            label: 'Link Style',
            isRequired: false,
            value: getDefaultListViewSubDisplayType(),
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: LIST_STYLE_OPTIONS,
            isSearchable: false,
            variant: 'horizontal',
        },
        gridSubDisplayType: {
            id: 'gridSubDisplayType',
            name: 'gridSubDisplayType',
            label: 'Link Style',
            isRequired: false,
            value: getDefaultGridSubDisplayType(),
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: GRID_STYLE_OPTIONS,
            isSearchable: false,
            variant: 'horizontal',
        },
        minGridColumns: {
            id: 'minGridColumns',
            name: 'minGridColumns',
            label: 'Min. grid columns',
            labelInfoText:
                'Minimum number of columns in the grid for mobile view. Large screens will auto-adjust based on available space.',
            isRequired: false,
            value: 2,
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: MIN_GRID_COLUMNS_OPTIONS,
            isSearchable: false,
        },
        carouselSubDisplayType: {
            id: 'carouselSubDisplayType',
            name: 'carouselSubDisplayType',
            label: 'Link Style',
            isRequired: false,
            value: getDefaultCarouselSubDisplayType(),
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: CAROUSEL_STYLE_OPTIONS,
            isSearchable: false,
            variant: 'horizontal',
        },

        // Border fields
        borderColor: {
            id: 'borderColor',
            name: 'borderColor',
            label: 'Border Color',
            isRequired: false,
            value: '',
            errorMessage: '',
            placeholder: '#000000',
            type: 'color',
        },
        borderWidth: {
            id: 'borderWidth',
            name: 'borderWidth',
            label: 'Border Width',
            isRequired: false,
            value: 0,
            errorMessage: '',
            placeholder: 'Please enter a valid number',
            type: 'dropdown',
            isShowBorder: true,
            isSearchable: false,
            enumValues: BORDER_WIDTH_OPTIONS,
            validate: 'width',
            dropdownWidth: '200px',
        },

        // Image style fields

        imageSize: {
            id: 'imageSize',
            name: 'imageSize',
            label: 'Image Size',
            isRequired: false,
            value: IMAGE_SIZE_VALUES.MEDIUM,
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: [
                { value: IMAGE_SIZE_VALUES.SMALL, label: 'S', width: '40px' },
                { value: IMAGE_SIZE_VALUES.MEDIUM, label: 'M', width: '40px' },
                // { value: IMAGE_SIZE_VALUES.LARGE, label: 'L', width: '40px' },
            ],
            isSearchable: false,
        },

        fillMode: {
            id: 'fillMode',
            name: 'fillMode',
            label: 'Image Fill',
            labelInfoText:
                "Fill will stretch the image to fill the container, while Fit will maintain the image's aspect ratio and may leave empty space and with that user can use Align configuration to align the image",
            isRequired: false,
            value: 'fit',
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: FILL_MODE_OPTIONS,
            isSearchable: false,
        },

        aspectRatio: {
            id: 'aspectRatio',
            name: 'aspectRatio',
            label: 'Aspect Ratio',
            isRequired: false,
            value: 'auto',
            errorMessage: '',
            placeholder: '',
            type: 'aspectRatio',
            enumValues: ASPECT_RATIO_OPTIONS,
            isSearchable: false,
            infoText:
                selectedModule?.name === MODULE_ITEMS.CONTAINER
                    ? 'Please do upload same size and same aspect ratio image with each link so that all links look same for better user experience'
                    : '',
        },

        horizontalAlignment: {
            id: 'horizontalAlignment',
            name: 'horizontalAlignment',
            labelInfoText:
                'Align the image to the left, right, or center of the container',
            label: 'Align',
            isRequired: false,
            value: 'left',
            errorMessage: '',
            placeholder: '',
            type: 'horizontalAlignment',
        },

        titleAlign: {
            id: 'titleAlign',
            name: 'titleAlign',
            label: 'Title Align',
            isRequired: false,
            value: 'left',
            errorMessage: '',
            placeholder: '',
            type: 'horizontalAlignment',
            items: [
                {
                    value: 'left',
                    icon: HorizontalLeftAlign,
                    label: 'Left',
                    tooltip: 'Align left',
                    width: '92px',
                },
                {
                    value: 'right',
                    icon: HorizontalRightAlign,
                    label: 'Right',
                    tooltip: 'Align right',
                    width: '92px',
                },
            ],
        },

        // Text style fields
        backgroundSelector: {
            id: 'backgroundSelector',
            name: 'backgroundSelector',
            label: 'Background',
            infoText: 'Choose background type and configure its properties',
            isRequired: true,
            type: 'backgroundColorSelector',
            backgroundColorLabel: 'Background Color',
            backgroundGradientLabel: 'Background Gradient',
            variant: 'horizontal',
            value: {
                backgroundType: BACKGROUND_TYPES.COLOR,
                backgroundColor: '#ffffff',
                backgroundGradient: commonStyles.colorWhite,
            },
            isImageAvailable: false,
        },
        backgroundColor: {
            label: 'Background Color',
            value: defaultWidgetBackgroundColor,
            type: 'color',
            isRequired: true,
        },
        accentColor: {
            label: 'Accent Color',
            value: '#0054FF',
            type: 'color',
            isRequired: true,
        },
        textAndIconOnAccentColor: {
            label: labelForTextAndIconOnAccentColor,
            value: '#FFFFFF',
            type: 'color',
            isRequired: true,
        },
        fontColor: {
            label: 'Label Color',
            value: defaultWidgetFontColor,
            type: 'color',
            isRequired: true,
        },
        textAndIconOnBackgroundColor: {
            label: 'Text Color',
            value: defaultWidgetFontColor,
            type: 'color',
            isRequired: true,
        },
        secondaryCardsPanelsColor: {
            label: 'Card Background Color',
            value: '#F5F5F5',
            type: 'color',
            isRequired: true,
        },
        textAndIconOnSecondaryCardsPanelsColor: {
            label: 'Text & Icon on Card',
            value: '#021231',
            type: 'color',
            isRequired: true,
        },

        linkStyle: {
            id: 'linkStyle',
            name: 'linkStyle',
            label: 'Link Style',
            isRequired: false,
            value: 'compact',
            errorMessage: '',
            placeholder: '',
            type: 'tabs',
            enumValues: [
                { value: 'compact', label: 'Compact', width: '80px' },
                { value: 'row', label: 'Row', width: '80px' },
            ],
            isSearchable: false,
        },

        labelFont: {
            id: 'labelFont',
            name: 'labelFont',
            label: labelFontText,
            infoText: 'Configure the font size and weight for title',
            isRequired: false,
            type: 'fontSelector',
            fontFamily: 'Inter',
            fontSize: '16',
            fontWeight: 'Medium',
            value: defaultWidgetLabelFont,
            fontFamilyOptions: getFontFamilyOptions(),
            fontSizeOptions,
            fontWeightOptions,
            variant: 'horizontal',
        },
        subtitleFont: {
            id: 'subtitleFont',
            name: 'subtitleFont',
            label: subtitleFontText,
            infoText: 'Configure the font size and weight for subtitle',
            isRequired: false,
            type: 'fontSelector',
            fontFamily: 'Inter',
            fontSize: '14',
            fontWeight: 'Regular',
            value: defaultWidgetSubtitleFont,
            fontFamilyOptions: getFontFamilyOptions(),
            fontSizeOptions,
            fontWeightOptions,
            variant: 'horizontal',
        },
    };

    // Always include ALL possible fields and styles - let UI decide what to show
    const visibleFields = getAllPossibleFields({
        selectedModule,
        isMenuPage,
        isWebDesigner,
        isConfigurable,
        isSubModuleInsideContainerWidget,
        activePage,
    });
    const visibleStyles = getAllPossibleStyles(selectedModule, isMenuPage);

    // Filter initial values based on visibility
    const filteredInitialValues = Object.keys(initialValues).reduce(
        (acc, key) => {
            if (visibleFields[key]) {
                acc[key] = initialValues[key];
            }
            return acc;
        },
        {}
    );

    const filteredInitialStyles = Object.keys(initialValuesForStyle).reduce(
        (acc, key) => {
            if (visibleStyles[key]) {
                acc[key] = initialValuesForStyle[key];
            }
            return acc;
        },
        {}
    );

    return { filteredInitialValues, filteredInitialStyles };
};

export const getInitialTabItems = ({ isWebDesigner, isShowAudience }) => {
    if (isWebDesigner) {
        return [
            {
                id: 0,
                label: labelsForTabItems.DETAILS,
                isActive: !isShowAudience,
            },
        ];
    }
    return [
        {
            id: 0,
            label: labelsForTabItems.DETAILS,
            isActive: !isShowAudience,
        },
        {
            id: 1,
            label: labelsForTabItems.AUDIENCE,
            isActive: isShowAudience,
        },
    ];
};

// Widget style options configuration
export const getWidgetDisplayTypeOptions = (moduleName) => {
    const displayTypeOptions = {
        [MODULE_ITEMS.LINKED_PAGE]: [
            {
                value: WIDGET_STYLE_TYPES.LINKED_PAGE.SIMPLE.value,
                label: WIDGET_STYLE_TYPES.LINKED_PAGE.SIMPLE.label,
                icon: 'simple-link',
                description: 'Basic link style with minimal design',
            },
            {
                value: WIDGET_STYLE_TYPES.LINKED_PAGE.CARD.value,
                label: WIDGET_STYLE_TYPES.LINKED_PAGE.CARD.label,
                icon: 'card-link',
                description: 'Card-style link with background and padding',
            },
            {
                value: WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.value,
                label: WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.label,
                icon: 'banner-link',
                description: 'Full-width banner style with image support',
            },
        ],
        [MODULE_ITEMS.CONTAINER]: [
            {
                value: WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value,
                label: WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.label,
                icon: 'list-view',
                description: 'Display items in a vertical list format',
            },
            {
                value: WIDGET_STYLE_TYPES.CONTAINER.GRID.value,
                label: WIDGET_STYLE_TYPES.CONTAINER.GRID.label,
                icon: 'grid-view',
                description: 'Display items in a grid layout',
            },
            {
                value: WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value,
                label: WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.label,
                icon: 'carousel-view',
                description:
                    'Display items in a horizontal scrollable carousel',
            },
            {
                value: WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value,
                label: WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.label,
                icon: 'slideshow-view',
                description: 'Display items as an auto-rotating slideshow',
            },
        ],
        [MODULE_ITEMS.PEOPLE]: [
            {
                value: WIDGET_STYLE_TYPES.PEOPLE.LIST.value,
                label: WIDGET_STYLE_TYPES.PEOPLE.LIST.label,
                icon: 'list-view',
                description: 'Display people in a vertical list format',
            },
            {
                value: WIDGET_STYLE_TYPES.PEOPLE.GRID.value,
                label: WIDGET_STYLE_TYPES.PEOPLE.GRID.label,
                icon: 'grid-view',
                description: 'Display people in a grid layout',
            },
            {
                value: WIDGET_STYLE_TYPES.PEOPLE.CAROUSEL.value,
                label: WIDGET_STYLE_TYPES.PEOPLE.CAROUSEL.label,
                icon: 'carousel-view',
                description:
                    'Display people in a horizontal scrollable carousel',
            },
            {
                value: WIDGET_STYLE_TYPES.PEOPLE.FEATURED.value,
                label: WIDGET_STYLE_TYPES.PEOPLE.FEATURED.label,
                icon: 'featured-icon',
                description: 'Display only the first person as featured',
            },
        ],
    };

    return displayTypeOptions[moduleName] || [];
};

// Get default display type for a module
export const getDefaultWidgetDisplayType = (moduleName) => {
    const defaultDisplayTypes = {
        [MODULE_ITEMS.LINKED_PAGE]: WIDGET_STYLE_TYPES.LINKED_PAGE.SIMPLE.value,
        [MODULE_ITEMS.CONTAINER]: WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value,
        [MODULE_ITEMS.PEOPLE]: WIDGET_STYLE_TYPES.PEOPLE.LIST.value,
    };

    return defaultDisplayTypes[moduleName] || null;
};

// Check if a module supports multiple display types
export const hasMultipleDisplayTypes = (moduleName) => {
    const displayTypeOptions = getWidgetDisplayTypeOptions(moduleName);
    return displayTypeOptions.length > 1;
};

// Get sub-display type options for LIST_VIEW
export const getListViewSubDisplayTypeOptions = () => [
    {
        value: WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.SIMPLE.value,
        label: WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.SIMPLE.label,
        icon: 'simple-link',
        description: 'Basic link style with minimal design',
    },
    {
        value: WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.CARD.value,
        label: WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.CARD.label,
        icon: 'card-link',
        description: 'Card-style link with background and padding',
    },
    {
        value: WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.POSTER.value,
        label: WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.POSTER.label,
        icon: 'banner-link',
        description: 'Full-width banner style with image support',
    },
];

// Get default sub-display type for LIST_VIEW
export const getDefaultListViewSubDisplayType = () =>
    WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.SIMPLE.value;

export const getDefaultGridSubDisplayType = () =>
    WIDGET_STYLE_TYPES.GRID_SUB_TYPES.CARD.value;

export const getDefaultCarouselSubDisplayType = () =>
    WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.SIMPLE.value;

export const isLinkedPagePosterConfig = (moduleName, displayType) =>
    moduleName === MODULE_ITEMS.LINKED_PAGE &&
    displayType === WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.value;

export const isPosterTextOnImageContainerConfig = ({
    displayType,
    listViewSubDisplayType,
    gridSubDisplayType,
    carouselSubDisplayType,
} = {}) => {
    if (!displayType) return false;

    if (displayType === WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value) {
        return true;
    }

    if (displayType === WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value) {
        return (
            (listViewSubDisplayType || getDefaultListViewSubDisplayType()) ===
            WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.POSTER.value
        );
    }

    if (displayType === WIDGET_STYLE_TYPES.CONTAINER.GRID.value) {
        return (
            (gridSubDisplayType || getDefaultGridSubDisplayType()) ===
            WIDGET_STYLE_TYPES.GRID_SUB_TYPES.POSTER.value
        );
    }

    if (displayType === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value) {
        return (
            (carouselSubDisplayType ||
                getDefaultCarouselSubDisplayType()) ===
            WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.POSTER.value
        );
    }

    return false;
};

// Get display type-specific fields for LINKED_PAGE, CONTAINER, and PEOPLE modules
export const getDisplayTypeSpecificStyles = (
    moduleName,
    selectedDisplayType,
    isMenuPage = false
) => {
    if (
        !moduleName ||
        !selectedDisplayType ||
        (moduleName !== MODULE_ITEMS.LINKED_PAGE &&
            moduleName !== MODULE_ITEMS.CONTAINER &&
            moduleName !== MODULE_ITEMS.PEOPLE)
    ) {
        return {};
    }

    const displayTypeSpecificStyles = {
        [WIDGET_STYLE_TYPES.LINKED_PAGE.SIMPLE.value]: {
            labelFont: true,
            fontColor: true,
            backgroundColor: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
        },
        [WIDGET_STYLE_TYPES.LINKED_PAGE.CARD.value]: {
            imageSize: true,
            fillMode: true,
            aspectRatio: true,
            backgroundColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
        },
        [WIDGET_STYLE_TYPES.LINKED_PAGE.POSTER.value]: {
            aspectRatio: true,
            fillMode: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
        },

        // CONTAINER styles - following the same pattern as LINKED_PAGE
        [WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value]: {
            labelFont: true,
            fontColor: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
            backgroundColor: true,
        },

        // LIST_VIEW sub-display types - same as LINKED_PAGE styles
        [WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.SIMPLE.value]: {
            labelFont: true,
            fontColor: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
            backgroundColor: true,
        },
        [WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.CARD.value]: {
            imageSize: true,
            fillMode: true,
            aspectRatio: true,
            backgroundColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
        },
        [WIDGET_STYLE_TYPES.LIST_VIEW_SUB_TYPES.POSTER.value]: {
            aspectRatio: true,
            fillMode: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
        },
        [WIDGET_STYLE_TYPES.CONTAINER.GRID.value]: {
            backgroundColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
        },
        // GRID sub-display types
        [WIDGET_STYLE_TYPES.GRID_SUB_TYPES.CARD.value]: {
            imageSize: true,
            fillMode: true,
            aspectRatio: true,
            backgroundColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
            minGridColumns: true,
        },
        [WIDGET_STYLE_TYPES.GRID_SUB_TYPES.POSTER.value]: {
            fillMode: true,
            aspectRatio: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
        },
        [WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value]: {
            backgroundColor: true,
            fontColor: true,
            labelFont: true,
        },
        // CAROUSEL sub-display types
        [WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.SIMPLE.value]: {
            labelFont: true,
            fontColor: true,
            backgroundColor: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
        },
        [WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.CARD.value]: {
            imageSize: true,
            fillMode: true,
            aspectRatio: true,
            backgroundColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
        },
        [WIDGET_STYLE_TYPES.CAROUSEL_SUB_TYPES.POSTER.value]: {
            fillMode: true,
            aspectRatio: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
        },
        [WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value]: {
            // Same as BANNER style
            aspectRatio: true,
            fillMode: true,
            fontColor: true,
            labelFont: true,
            subtitleFont: true,
            accentColor: true,
            textAndIconOnAccentColor: true,
        },

        // PEOPLE widget styles
        [WIDGET_STYLE_TYPES.PEOPLE.LIST.value]: {
            fontColor: true,
        },
        [WIDGET_STYLE_TYPES.PEOPLE.GRID.value]: {
            fontColor: true,
        },
        [WIDGET_STYLE_TYPES.PEOPLE.CAROUSEL.value]: {
            fontColor: true,
        },
        [WIDGET_STYLE_TYPES.PEOPLE.FEATURED.value]: {
            fontColor: true,
        },
    };

    return displayTypeSpecificStyles[selectedDisplayType] || {};
};

// Get ALL possible fields for a module across all display types
export const getAllPossibleFields = ({
    selectedModule,
    isMenuPage,
    isWebDesigner,
    isConfigurable,
    isSubModuleInsideContainerWidget,
    activePage,
}) => {
    const moduleName = selectedModule?.name;

    // Get base fields that are always visible regardless of display type
    const baseFields = getVisibleFields({
        selectedModule,
        isMenuPage,
        isWebDesigner,
        isConfigurable,
        isSubModuleInsideContainerWidget,
        activePage,
    });

    // For modules with multiple display types, include fields from all display types
    if (hasMultipleDisplayTypes(moduleName)) {
        const displayTypeOptions = getWidgetDisplayTypeOptions(moduleName);

        // Merge fields from all possible display types
        displayTypeOptions.forEach((displayTypeOption) => {
            const fieldsForDisplayType = getVisibleFields({
                selectedModule,
                isMenuPage,
                isWebDesigner,
                isConfigurable,
                isSubModuleInsideContainerWidget,
                activePage,
                selectedDisplayType: displayTypeOption.value,
            });

            // Merge display type-specific fields
            Object.keys(fieldsForDisplayType).forEach((fieldKey) => {
                if (fieldsForDisplayType[fieldKey]) {
                    baseFields[fieldKey] = true;
                }
            });
        });
    }

    return baseFields;
};

// Get ALL possible styles for a module across all display types
export const getAllPossibleStyles = (selectedModule, isMenuPage = false) => {
    const moduleName = selectedModule?.name;

    // Get base styles that are always visible regardless of display type
    const baseStyles = getVisibleStyles(selectedModule, null, isMenuPage);

    // For modules with multiple display types, include styles from all display types
    if (hasMultipleDisplayTypes(moduleName)) {
        const displayTypeOptions = getWidgetDisplayTypeOptions(moduleName);

        const mergeSubTypeStyles = (displayType, subOptions, subTypeParam) => {
            subOptions.forEach((subOption) => {
                const args = [
                    selectedModule,
                    displayType,
                    isMenuPage,
                    subTypeParam === 'list' ? subOption.value : null,
                    subTypeParam === 'grid' ? subOption.value : null,
                    subTypeParam === 'carousel' ? subOption.value : null,
                ];
                const stylesForSub = getVisibleStyles(...args);
                Object.keys(stylesForSub).forEach((styleKey) => {
                    if (stylesForSub[styleKey]) {
                        baseStyles[styleKey] = true;
                    }
                });
            });
        };

        displayTypeOptions.forEach((displayTypeOption) => {
            const dtValue = displayTypeOption.value;
            if (
                moduleName === MODULE_ITEMS.CONTAINER &&
                dtValue === WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value
            ) {
                mergeSubTypeStyles(
                    dtValue,
                    getListViewSubDisplayTypeOptions(),
                    'list'
                );
            } else if (
                moduleName === MODULE_ITEMS.CONTAINER &&
                dtValue === WIDGET_STYLE_TYPES.CONTAINER.GRID.value
            ) {
                mergeSubTypeStyles(dtValue, GRID_STYLE_OPTIONS, 'grid');
            } else if (
                moduleName === MODULE_ITEMS.CONTAINER &&
                dtValue === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value
            ) {
                mergeSubTypeStyles(dtValue, CAROUSEL_STYLE_OPTIONS, 'carousel');
            } else {
                const stylesForDisplayType = getVisibleStyles(
                    selectedModule,
                    dtValue,
                    isMenuPage
                );
                Object.keys(stylesForDisplayType).forEach((styleKey) => {
                    if (stylesForDisplayType[styleKey]) {
                        baseStyles[styleKey] = true;
                    }
                });
            }
        });
    }

    return baseStyles;
};

export const getModuleDefinitionItemByModuleName = (moduleName) => {
    // Flatten all modules from MODULES object to find the selected module
    const allModules = Object.values(MODULES).flat();
    let selectedModuleFromOverlay = allModules.find(
        (item) => item.id === moduleName
    );

    // If not found in MODULES, check LEGACY_MODULES for backward compatibility
    if (!selectedModuleFromOverlay) {
        selectedModuleFromOverlay = LEGACY_MODULES.find(
            (item) => item.id === moduleName
        );
    }

    // Map the MODULES structure to the expected selectedModule structure
    const selectedModuleItem = selectedModuleFromOverlay
        ? {
              name: selectedModuleFromOverlay.id,
              label: selectedModuleFromOverlay.label,
              data: selectedModuleFromOverlay.data || {},
          }
        : null;

    return selectedModuleItem;
};

// Function to generate tooltip text for widgets
export const getWidgetTooltipText = (widgetItem) => {
    const selectedModuleName = widgetItem?.settings?.selectedModuleName;

    // Get the main module label
    const moduleOption =
        getModuleDefinitionItemByModuleName(selectedModuleName);

    let tooltipText = moduleOption?.label || '';
    let hasSubType = false;

    if (selectedModuleName === MODULE_ITEMS.CONTAINER) {
        const displayType = widgetItem?.settings?.displayType;
        const imageSize = widgetItem?.settings?.styles?.imageSize;

        // Find the matching style type by value
        const viewTypeEntry = Object.values(WIDGET_STYLE_TYPES.CONTAINER).find(
            (type) => type.value === displayType
        );
        const viewTypeLabel = viewTypeEntry?.label || '';

        if (viewTypeLabel) {
            hasSubType = true;
            // If carousel with Pill size, show only "Carousel - Pill"
            if (
                displayType === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value &&
                imageSize === IMAGE_SIZE_VALUES.PILL
            ) {
                tooltipText = `${viewTypeLabel} - Pill`;
            } else {
                tooltipText = viewTypeLabel;
            }
        }
    }

    if (selectedModuleName === MODULE_ITEMS.PEOPLE) {
        const displayType = widgetItem?.settings?.displayType;

        // Find the matching style type by value
        const viewTypeEntry = Object.values(WIDGET_STYLE_TYPES.PEOPLE).find(
            (type) => type.value === displayType
        );
        const viewTypeLabel = viewTypeEntry?.label || '';

        if (viewTypeLabel) {
            hasSubType = true;
            tooltipText = viewTypeLabel;
        }
    }

    if (selectedModuleName === MODULE_ITEMS.LINKED_PAGE) {
        const displayType = widgetItem?.settings?.displayType;

        // Find the matching style type by value
        const viewTypeEntry = Object.values(
            WIDGET_STYLE_TYPES.LINKED_PAGE
        ).find((type) => type.value === displayType);
        const viewTypeLabel = viewTypeEntry?.label || '';

        if (viewTypeLabel) {
            hasSubType = true;
            tooltipText = viewTypeLabel;
        }
    }

    return tooltipText;
};

export const getWidgetTypographyConfiguration = (styles) => {
    const { store } = storeAndPersistor;
    const typography = store?.getState()?.eventWrapper?.typography;

    const globalFontFamily = typography?.fontFamily || 'Inter';
    const labelFontFromTypography = typography?.typographyItems?.find(
        (item) => item.id === 'buttons'
    );
    const subtitleFontFromTypography = typography?.typographyItems?.find(
        (item) => item.id === 'secondaryText'
    );
    const widgetLabelFont =
        styles?.labelFont || labelFontFromTypography || defaultWidgetLabelFont;
    const widgetSubtitleFont =
        styles?.subtitleFont ||
        subtitleFontFromTypography ||
        defaultWidgetSubtitleFont;
    const labelFontFamily =
        (isWidgetLevelFontFamilyEnabled && widgetLabelFont.fontFamily) ||
        globalFontFamily;
    const subtitleFontFamily =
        (isWidgetLevelFontFamilyEnabled && widgetSubtitleFont.fontFamily) ||
        globalFontFamily;
    const labelFont = {
        fontSize: widgetLabelFont.fontSize,
        fontWeight: resolveFontWeight(
            labelFontFamily,
            widgetLabelFont.fontWeight
        ),
        fontFamily: labelFontFamily,
    };
    const subtitleFont = {
        fontSize: widgetSubtitleFont.fontSize,
        fontWeight: resolveFontWeight(
            subtitleFontFamily,
            widgetSubtitleFont.fontWeight
        ),
        fontFamily: subtitleFontFamily,
    };
    if (labelFontFamily) {
        loadEventFont(labelFontFamily).catch(() => {
            // Failed to load font - will use fallback
        });
    }
    if (subtitleFontFamily !== labelFontFamily) {
        loadEventFont(subtitleFontFamily).catch(() => {
            // Failed to load font - will use fallback
        });
    }
    return {
        labelFont,
        subtitleFont,
    };
};

export const getWidgetFontColorsConfiguration = (styles) => {
    const { store } = storeAndPersistor;
    const themeColors = store?.getState()?.eventWrapper?.themeColors;
    const fontColor = styles?.fontColor || themeColors?.textAndIconOnCardColor;
    const textAndIconOnBackgroundColor =
        styles?.textAndIconOnBackgroundColor ||
        themeColors?.textAndIconOnBackgroundColor;
    const backgroundColor =
        styles?.backgroundColor || themeColors?.cardsPanelsColor;
    const secondaryCardsPanelsColor =
        styles?.secondaryCardsPanelsColor ||
        themeColors?.secondaryCardsPanelsColor;
    const textAndIconOnSecondaryCardsPanelsColor =
        styles?.textAndIconOnSecondaryCardsPanelsColor ||
        themeColors?.textAndIconOnSecondaryCardsPanelsColor;
    // Get accent colors from styles or event theme colors
    const accentColor = styles?.accentColor || themeColors?.accentColor;
    const textAndIconOnAccentColor =
        styles?.textAndIconOnAccentColor ||
        themeColors?.textAndIconOnAccentColor;

    const backgroundStyles = {
        backgroundColor:
            styles?.backgroundColor || themeColors?.cardsPanelsColor,
    };

    const config = {
        fontColor,
        textAndIconOnBackgroundColor,
        backgroundColor,
        accentColor,
        textAndIconOnAccentColor,
        backgroundStyles,
        secondaryCardsPanelsColor,
        textAndIconOnSecondaryCardsPanelsColor,
    };

    return config;
};

// Re-export constants from appDesignerConstants to maintain backward compatibility
// These constants are defined in a separate file to avoid circular dependencies
export {
    colorKeys,
    BACKGROUND_TYPES,
    widgetColorToThemeColorMap,
    DEFAULT_ADVANCED_COLORS_LIGHT,
    DEFAULT_ADVANCED_COLORS_DARK,
    DEFAULT_EVENT_STYLES,
} from './appDesignerConstants';

/**
 * Converts hex color to RGB values
 * @param hex - The hex color (e.g., '#FF5733' or 'FF5733')
 * @returns Object with r, g, b values (0-255)
 */
export const hexToRgb = (hex) => {
    try {
        if (!hex) return null;
        const cleanHex = hex?.replace('#', '');
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return { r, g, b };
    } catch (error) {
        console.error('Error converting hex to rgb', error);
        return null;
    }
};

/**
 * Calculates the relative luminance of a color
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Luminance value (0-1)
 */
export const calculateLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map((c) => {
        const val = c / 255;
        return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Determines if a color is dark based on its luminance
 * @param color - The hex color (e.g., '#FF5733')
 * @param threshold - Luminance threshold (default: 0.5, lower = darker)
 * @returns True if the color is dark
 */
export const isColorDark = (color, threshold = 0.5) => {
    if (!color) return false;

    const rgb = hexToRgb(color);
    if (!rgb) return false;

    const luminance = calculateLuminance(rgb.r, rgb.g, rgb.b);
    return luminance < threshold;
};

/**
 * Brightens a color by a given amount
 * @param color - The hex color (e.g., '#FF5733')
 * @param amount - Amount to brighten (0-100, default: 20)
 * @returns Brightened hex color
 */
export const brightenColor = (color, amount = 20) => {
    if (!color) return color;

    const rgb = hexToRgb(color);
    if (!rgb) return color;

    const brighten = (value) => {
        const brightened = Math.min(
            255,
            value + Math.round((255 - value) * (amount / 100))
        );
        return brightened;
    };

    const r = brighten(rgb.r).toString(16).padStart(2, '0');
    const g = brighten(rgb.g).toString(16).padStart(2, '0');
    const b = brighten(rgb.b).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
};

/**
 * Darkens a color by a given amount
 * @param color - The hex color (e.g., '#FF5733')
 * @param amount - Amount to darken (0-100, default: 20)
 * @returns Darkened hex color
 */
export const darkenColor = (color, amount = 20) => {
    if (!color) return color;

    const rgb = hexToRgb(color);
    if (!rgb) return color;

    const darken = (value) => {
        const darkened = Math.max(
            0,
            value - Math.round(value * (amount / 100))
        );
        return darkened;
    };

    const r = darken(rgb.r).toString(16).padStart(2, '0');
    const g = darken(rgb.g).toString(16).padStart(2, '0');
    const b = darken(rgb.b).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
};

/**
 * Returns a computed border (e.g. "1px solid #abc") for a given background color.
 * If background is dark → brightened border; if light → darkened border.
 *
 * @param {Object} params
 * @param {string} params.backgroundColor - Background color in hex format
 * @param {number|null} params.adjustAmount - Brighten/darken amount (default: 30 dark, 15 light)
 * @param {boolean} params.skipBorderValidation - When true, ignores the global isShowBorderWithWidget flag
 * @param {boolean} params.returnColorOnly - When true, returns raw color string instead of full border
 * @returns {string} Full border string like "1px solid #hex" / "none", or raw color / null when returnColorOnly
 */
export const getDynamicBorder = ({
    backgroundColor,
    adjustAmount = null,
    skipBorderValidation = false,
    returnColorOnly = false,
} = {}) => {
    if (!skipBorderValidation && !isShowBorderWithWidget) {
        return returnColorOnly ? null : 'none';
    }
    if (!backgroundColor) {
        return returnColorOnly ? null : 'none';
    }

    const { store } = storeAndPersistor;
    const themeColors = store?.getState()?.eventWrapper?.themeColors;

    const bgColor =
        backgroundColor === 'transparent'
            ? themeColors?.backgroundColor
            : backgroundColor;

    const borderColor = isColorDark(bgColor)
        ? brightenColor(bgColor, adjustAmount || 30)
        : darkenColor(bgColor, adjustAmount || 15);

    if (returnColorOnly) return borderColor || null;
    return borderColor ? `1px solid ${borderColor}` : 'none';
};

/**
 * Adds opacity to a color by appending a hex opacity value
 * @param color - The base color in hex format (e.g., '#FF5733' or 'FF5733')
 * @param opacity - The opacity value as a hex string (e.g., '0A', '8F', 'E6', 'FF')
 *                  Common values: '00' (0%), '0A' (4%), '1A' (10%), '33' (20%), '4D' (30%),
 *                                 '66' (40%), '80' (50%), '99' (60%), 'B3' (70%), 'CC' (80%),
 *                                 'E6' (90%), 'FF' (100%), '8F' (56%)
 * @returns The color with opacity appended (e.g., '#FF57330A')
 */
export const addColorOpacity = (color, opacity) => {
    if (!color) return color;

    // Remove '#' if present
    const cleanColor = color.startsWith('#') ? color.slice(1) : color;

    // Append opacity and add '#' back
    return `#${cleanColor}${opacity}`;
};

/**
 * Creates a linear gradient string with different opacity levels
 * @param color - The base color in hex format
 * @param startOpacity - Opacity at the start of gradient (default: '00' for transparent)
 * @param midOpacity - Opacity at the middle of gradient (default: 'E6' for 90%)
 * @param endOpacity - Opacity at the end of gradient (default: 'FF' for 100%)
 * @returns A CSS linear-gradient string
 */
export const createColorGradient = (
    color,
    startOpacity = '00',
    midOpacity = 'E6',
    endOpacity = 'FF'
) => {
    const colorWithStart = addColorOpacity(color, startOpacity);
    const colorWithMid = addColorOpacity(color, midOpacity);
    const colorWithEnd = addColorOpacity(color, endOpacity);

    return `linear-gradient(180deg, ${colorWithStart} -5.97%, ${colorWithMid} 25.82%, ${colorWithEnd} 100%)`;
};

/**
 * Creates a vertical gradient for top overlay (title area)
 * Keeps a solid overlay behind the full (variable-height) title area, then fades
 * to transparent in a small band at the bottom edge.
 * @param color - The base background color in hex format
 * @returns A CSS linear-gradient string for top overlay
 */
export const createTopGradientOverlay = (color) => {
    if (!color) return 'transparent';

    // Keep the same opacity behind the whole (variable-height) title area,
    // and only fade to transparent in a small band at the edge.
    const colorWith56Opacity = addColorOpacity(color, '8F'); // 56% opacity
    const colorTransparent = addColorOpacity(color, '00'); // 0% opacity
    const fadeSize = '24px';

    return `linear-gradient(180deg, ${colorWith56Opacity} 0%, ${colorWith56Opacity} calc(100% - ${fadeSize}), ${colorTransparent} 100%)`;
};

/**
 * Creates a vertical gradient for bottom overlay (description area)
 * Fades in quickly at the top edge, then keeps a solid overlay behind the full
 * (variable-height) description area.
 * @param color - The base background color in hex format
 * @returns A CSS linear-gradient string for bottom overlay
 */
export const createBottomGradientOverlay = (color) => {
    if (!color) return 'transparent';

    const colorTransparent = addColorOpacity(color, '00'); // 0% opacity
    const colorWith56Opacity = addColorOpacity(color, '8F'); // 56% opacity
    const fadeSize = '24px';

    // Fade in quickly at the top edge, then keep solid opacity behind text.
    return `linear-gradient(180deg, ${colorTransparent} 0%, ${colorWith56Opacity} ${fadeSize}, ${colorWith56Opacity} 100%)`;
};

// ── Temporary-ID helpers for locally-created child widgets (e.g. links) ──

const TEMP_WIDGET_ID_PREFIX = 'temp_';

export const isTempWidgetId = (id) =>
    typeof id === 'string' && id.startsWith(TEMP_WIDGET_ID_PREFIX);

export const generateTempWidgetId = () =>
    `${TEMP_WIDGET_ID_PREFIX}${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

/**
 * Returns the next available name for a child widget (e.g. link).
 * Produces "New Link", "New Link 2", "New Link 3", …
 */
export const getNextChildWidgetName = (
    existingItems,
    baseName = 'New Link'
) => {
    const existingNames = new Set(existingItems.map((item) => item.name));
    if (!existingNames.has(baseName)) return baseName;
    let counter = 2;
    while (existingNames.has(`${baseName} ${counter}`)) {
        counter += 1;
    }
    return `${baseName} ${counter}`;
};

/**
 * Validates an array of child link widgets.
 * Mirrors the per-field validation in CustomLinkItem:
 *  1. Target value is required (must not be empty).
 *  2. For URL / EXTERNAL_URL types the value must be a valid URL.
 *
 * @returns {{ isValid: boolean, invalidLinkIds: string[] }}
 */
export const validateChildLinks = (links = []) => {
    const invalidLinkIds = [];
    const engagementToolValues = Object.values(ENGAGEMENT_TOOLS_TYPES);

    links.forEach((link) => {
        const linkName = (link.name || '').trim();
        const linkedPage = link.settings?.linkedPageData?.linkedPage || '';
        const linkedPageValue =
            link.settings?.linkedPageData?.linkedPageValue || '';

        // Validate required fields: name and target
        if (!linkName || !linkedPageValue) {
            invalidLinkIds.push(link.id);
        } else if (
            isUrlLikeLinkedPage(linkedPage) &&
            !validateLink(linkedPageValue)
        ) {
            invalidLinkIds.push(link.id);
        } else if (
            engagementToolValues.includes(linkedPageValue?.split('/')[0]) &&
            linkedPageValue?.split('/').length !== 3
        ) {
            invalidLinkIds.push(link.id);
        }
    });
    return { isValid: invalidLinkIds.length === 0, invalidLinkIds };
};

/**
 * Generates a unique widget name based on module label and existing widgets
 * @param module - The module with id and label (e.g., BANNER_IMAGE, TEXT, ACCORDION, etc.)
 * @param widgets - Array of existing widgets
 * @returns A unique widget name (e.g., "BANNER_IMAGE_1", "TEXT_2", "ACCORDION_3", etc.)
 */
export const generateWidgetName = (module, widgets = []) => {
    // Count how many widgets with the same label already exist
    const sameTypeWidgets = widgets.filter((widget) => {
        const selectedModuleName = widget?.settings?.selectedModuleName || '';
        return selectedModuleName
            .toLowerCase()
            .startsWith(module.id.toLowerCase());
    });
    const count = sameTypeWidgets.length + 1;
    return `${module.label} Module ${count !== 1 ? count : ''}`;
};

/**
 * Builds the common widget data object (name, description, settings) from form state.
 * Used by both the live preview useEffect and handleSaveDetails to ensure a single
 * source of truth for widget configuration.
 *
 * @returns {{ name: string, description: string, settings: object }}
 */
export const buildWidgetData = ({
    fields,
    mobileStyles,
    selectedModule,
    isShowTitle,
    isShowTitleConfiguration,
    isShowChildTitle,
    isShowChildTitleConfiguration,
    isMenuPage,
    isDefaultLink,
    isConfigurable,
    selectedLinkedPage,
    selectedTitleDropdownItem,
    isEngagementToolModule,
    isShowFullWidthModuleToggleForWeb,
}) => {
    const moduleName = selectedModule?.name;
    const isContainerModule = moduleName === MODULE_ITEMS.CONTAINER;

    const settings = {
        isShowSeparatorLine: fields?.isShowSeparatorLine?.value || false,
        isShowTitle: isShowTitleConfiguration ? isShowTitle : true,
        selectedModuleName: moduleName,
        styles: {},
    };

    if (fields?.displayType?.value) {
        settings.displayType = fields.displayType.value;
    }

    if (isMenuPage) {
        settings.isDefaultLinks = isDefaultLink;
        settings.isConfigurable = isConfigurable;
        if (isConfigurable) {
            settings.isVisible = fields?.isVisible?.value;
        }
    }

    if (fields?.isShowFullWidthBanner) {
        settings.isShowFullWidthBanner = fields.isShowFullWidthBanner.value;
    }

    // Sub-display types belong at the settings level, not inside styles.
    // They are handled explicitly in the container-specific block below.
    const SUB_DISPLAY_TYPE_KEYS = new Set([
        'listViewSubDisplayType',
        'gridSubDisplayType',
        'carouselSubDisplayType',
    ]);

    // Extract mobile styles into settings.styles (excluding sub-display types)
    if (mobileStyles) {
        Object.keys(mobileStyles).forEach((key) => {
            if (SUB_DISPLAY_TYPE_KEYS.has(key)) return;
            if (mobileStyles[key]?.value !== undefined) {
                if (mobileStyles[key]?.type === 'fontSelector') {
                    const val = mobileStyles[key].value;
                    settings.styles[key] = {
                        fontFamily: val.fontFamily,
                        fontSize: val.fontSize,
                        fontWeight: val.fontWeight,
                    };
                } else {
                    settings.styles[key] = mobileStyles[key].value;
                }
            }
        });
    }

    // Title dropdown for people/session/material modules
    if (
        !isEngagementToolModule &&
        TITLE_SELECTION_ALLOWED_MODULES.includes(moduleName) &&
        selectedTitleDropdownItem?.length > 0
    ) {
        settings.selectedTitleDropdownItem = selectedTitleDropdownItem.map(
            (item) => item.value
        );
    }

    // People widget fields
    if (moduleName === MODULE_ITEMS.PEOPLE) {
        if (fields?.peopleSubtitleFields) {
            settings.peopleSubtitleFields =
                fields.peopleSubtitleFields.value.map(({ id }) => id);
        }
        if (fields?.peopleProfile) {
            settings.peopleProfile = fields.peopleProfile.value;
        }
        if (fields?.sortedBy) {
            settings.sortedBy = fields.sortedBy.value;
        }
    }

    // Linked page / Banner - linkedPageData
    if (
        moduleName === MODULE_ITEMS.LINKED_PAGE ||
        moduleName === MODULE_ITEMS.BANNER
    ) {
        settings.linkedPageData = {
            linkedPageValue: fields?.linkedPage?.value || '',
            linkedPage: selectedLinkedPage || '',
        };
    }

    // Text / Accordion rich text
    if (fields?.text) {
        settings.textData = { text: fields.text.value };
    }

    // Accordion
    if (moduleName === MODULE_ITEMS.ACCORDION) {
        settings.isExpandByDefault = fields?.isExpandByDefault?.value || false;
    }

    // Image widget
    if (moduleName === MODULE_ITEMS.IMAGE) {
        settings.isOpenImageOnClick =
            fields?.isOpenImageOnClick?.value || false;
    }

    // Banner widget button data
    if (moduleName === MODULE_ITEMS.BANNER) {
        settings.bannerData = {
            includeButton: fields?.includeButton?.value || false,
            buttonLabel: fields?.buttonLabel?.value || '',
        };
    }

    // Video widget
    if (moduleName === MODULE_ITEMS.VIDEO) {
        if (fields?.showControls)
            settings.showControls = fields.showControls.value;
        if (fields?.autoplay) settings.autoplay = fields.autoplay.value;
        if (fields?.muteAudio) settings.muteAudio = fields.muteAudio.value;
    }

    // Full width module toggle (web)
    if (isShowFullWidthModuleToggleForWeb) {
        settings.isShowFullWidthModule = fields?.isShowFullWidthModule?.value;
    }

    // Container-specific settings
    if (isContainerModule && fields?.displayType?.value) {
        const displayType = fields.displayType.value;

        if (
            displayType === WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value &&
            mobileStyles?.listViewSubDisplayType
        ) {
            settings.listViewSubDisplayType =
                mobileStyles.listViewSubDisplayType.value;
        }

        if (
            displayType === WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
            mobileStyles?.gridSubDisplayType
        ) {
            settings.gridSubDisplayType = mobileStyles.gridSubDisplayType.value;
        }

        if (
            displayType === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value &&
            mobileStyles?.carouselSubDisplayType
        ) {
            settings.carouselSubDisplayType =
                mobileStyles.carouselSubDisplayType.value;
        }

        if (
            displayType === WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
            fields.useImageAsIcon
        ) {
            settings.useImageAsIcon = fields.useImageAsIcon.value;
        }

        if (displayType === WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value) {
            if (
                fields.pillType &&
                mobileStyles?.imageSize?.value === IMAGE_SIZE_VALUES.PILL
            ) {
                settings.pillType = fields.pillType.value;
            }
        }

        if (displayType === WIDGET_STYLE_TYPES.CONTAINER.SLIDESHOW.value) {
            settings.isRotationOn = fields.isRotationOn?.value || false;
            settings.rotationInterval = +fields.rotationInterval?.value || 5;
        }

        if (isShowChildTitleConfiguration) {
            settings.isShowChildTitle = isShowChildTitle;
        }
    }

    // Directory linkToFullPageList
    if (LINK_TO_FULL_PAGE_MODULES.includes(moduleName)) {
        settings.linkToFullPageList = fields?.linkToFullPageList?.value;
    }

    return {
        name: fields?.name?.value || '',
        description: fields?.description?.value || '',
        settings,
    };
};

/**
 * Post-processes styles built by buildWidgetData for saving to the API.
 * Removes styles that match event defaults so the API only stores overrides.
 *
 * @param {object} rawStyles - The styles object from buildWidgetData().settings.styles
 * @param {object} mobileStyles - Raw deviceStyles[DEVICE_TYPES.MOBILE] with type metadata
 * @param {object} options
 * @returns {object} Filtered styles object ready for the save API
 */
export const filterStylesForSave = (
    rawStyles,
    mobileStyles,
    {
        eventTypography,
        eventThemeColors,
        selectedModule,
        isMenuPage,
        currentDisplayType,
    }
) => {
    if (!mobileStyles) return rawStyles;

    const filtered = {};

    let effectiveDisplayType = currentDisplayType;
    if (selectedModule?.name === MODULE_ITEMS.CONTAINER && currentDisplayType) {
        if (
            currentDisplayType ===
                WIDGET_STYLE_TYPES.CONTAINER.LIST_VIEW.value &&
            mobileStyles?.listViewSubDisplayType?.value
        ) {
            effectiveDisplayType = mobileStyles.listViewSubDisplayType.value;
        } else if (
            currentDisplayType === WIDGET_STYLE_TYPES.CONTAINER.GRID.value &&
            mobileStyles?.gridSubDisplayType?.value
        ) {
            effectiveDisplayType = mobileStyles.gridSubDisplayType.value;
        } else if (
            currentDisplayType ===
                WIDGET_STYLE_TYPES.CONTAINER.CAROUSEL.value &&
            mobileStyles?.carouselSubDisplayType?.value
        ) {
            effectiveDisplayType = mobileStyles.carouselSubDisplayType.value;
        }
    }

    const validStylesForDisplayType = effectiveDisplayType
        ? getDisplayTypeSpecificStyles(
              selectedModule?.name,
              effectiveDisplayType,
              isMenuPage
          )
        : {};

    const getEventFontForKey = (key) => {
        if (!eventTypography?.typographyItems) return null;
        const keyToTypographyId = {
            labelFont: 'buttons',
            subtitleFont: 'secondaryText',
        };
        const typographyId = keyToTypographyId[key];
        if (!typographyId) return null;
        const typographyItem = eventTypography.typographyItems.find(
            (item) => item.id === typographyId
        );
        if (!typographyItem) return null;
        return {
            fontFamily: eventTypography.fontFamily || 'Inter',
            fontSize: typographyItem.fontSize,
            fontWeight: typographyItem.fontWeight,
        };
    };

    Object.keys(mobileStyles).forEach((key) => {
        if (mobileStyles[key]?.value === undefined) return;

        const styleEntry = mobileStyles[key];

        // Skip styles not valid for current display type
        if (currentDisplayType && !validStylesForDisplayType[key]) return;

        // Skip font selectors matching event typography
        if (styleEntry.type === 'fontSelector') {
            const eventFont = getEventFontForKey(key);
            if (eventFont) {
                const familyMatch =
                    styleEntry.fontFamily === eventFont.fontFamily;
                const sizeMatch =
                    Number(styleEntry.fontSize) === Number(eventFont.fontSize);
                const weightMatch =
                    String(styleEntry.fontWeight) ===
                    String(eventFont.fontWeight);
                if (familyMatch && sizeMatch && weightMatch) return;
            }

            // Exclude fontFamily when feature flag is disabled
            if (!isWidgetLevelFontFamilyEnabled) {
                const { fontFamily, ...fontWithoutFamily } =
                    styleEntry.value || {};
                filtered[key] = fontWithoutFamily;
                return;
            }
        }

        // Skip color fields matching event theme
        if (styleEntry.type === 'color' && eventThemeColors) {
            const themeColorProperty = widgetColorToThemeColorMap[key];
            if (themeColorProperty) {
                const eventColor = eventThemeColors[themeColorProperty];
                if (
                    eventColor &&
                    styleEntry.value?.toLowerCase() ===
                        eventColor?.toLowerCase()
                ) {
                    return;
                }
            }
        }

        filtered[key] = styleEntry.value;
    });

    return filtered;
};

const EVENT_FIELD_ADJUST_AMOUNT = 10;
const EVENT_FIELD_LIGHTNESS_THRESHOLD = 90;

const adjustEventFieldColor = (color) => {
    if (!color) return color;

    const { l: lightness } = hexToHSL(color);

    if (lightness <= EVENT_FIELD_LIGHTNESS_THRESHOLD) {
        return brightenColor(color, EVENT_FIELD_ADJUST_AMOUNT);
    }

    return darkenColor(color, EVENT_FIELD_ADJUST_AMOUNT);
};

export const getEventFieldColors = (themeColors) => {
    const fieldBgColor = adjustEventFieldColor(themeColors?.cardsPanelsColor);
    // uncomment this when we have a way to adjust the text color
    // const fieldTextColor = adjustEventFieldColor(
    //     themeColors?.textAndIconOnCardColor
    // );
    const fieldTextColor = themeColors?.textAndIconOnCardColor;
    return { fieldBgColor, fieldTextColor };
};

/**
 * CSS custom properties for `.event-themed-fields` (buttons, inputs, links).
 * Apply on a container (or portaled popup) so children resolve accent/card vars
 * even when body is not `data-event-themed` (e.g. App Designer CMS preview).
 */
export const getEventThemeCSSVars = (themeColors) => {
    if (!themeColors) return {};
    const { fieldBgColor, fieldTextColor } = getEventFieldColors(themeColors);
    return {
        '--event-field-error-text-color': themeColors?.alertsWarningsColor,
        '--event-field-bg-color': fieldBgColor,
        '--event-field-text-color': fieldTextColor,
        '--event-field-border-color': getDynamicBorder({
            backgroundColor: fieldBgColor,
            returnColorOnly: true,
        }),
        '--event-accent-color': themeColors?.accentColor,
        '--event-card-bg-color': themeColors?.cardsPanelsColor,
        '--event-card-text-color': themeColors?.textAndIconOnCardColor,
        '--event-card-border-color': getDynamicBorder({
            backgroundColor: themeColors?.cardsPanelsColor,
            returnColorOnly: true,
        }),
        '--event-secondary-card-bg-color':
            themeColors?.secondaryCardsPanelsColor,
        '--event-secondary-card-text-color':
            themeColors?.textAndIconOnSecondaryCardsPanelsColor,
        '--event-accent-text-color': themeColors?.textAndIconOnAccentColor,
        '--event-alert-color': themeColors?.alertsWarningsColor,
        '--event-alert-text-color':
            themeColors?.textAndIconOnAlertsWarningsColor,
    };
};

/** Designer hub right-panel resize (DesignerEditorLayout) */
export const DESIGNER_EDITOR_RIGHT_WIDTH_STORAGE_KEY =
    'designer-editor-right-width-v2';
export const DESIGNER_EDITOR_RIGHT_WIDTH_MIN = 320;
export const DESIGNER_EDITOR_RIGHT_WIDTH_MAX = 720;
/** Open at max; users can drag the grip to narrow the panel. */
export const DESIGNER_EDITOR_RIGHT_WIDTH_DEFAULT =
    DESIGNER_EDITOR_RIGHT_WIDTH_MAX;

export const clampDesignerEditorRightWidth = (width) =>
    Math.min(
        DESIGNER_EDITOR_RIGHT_WIDTH_MAX,
        Math.max(DESIGNER_EDITOR_RIGHT_WIDTH_MIN, width)
    );

export const getStoredDesignerEditorRightWidth = () => {
    try {
        const stored = Number(
            localStorage.getItem(DESIGNER_EDITOR_RIGHT_WIDTH_STORAGE_KEY)
        );
        if (Number.isFinite(stored)) {
            return clampDesignerEditorRightWidth(stored);
        }
    } catch (e) {
        // ignore
    }
    return DESIGNER_EDITOR_RIGHT_WIDTH_DEFAULT;
};

/** Designer hub left-panel resize (Registration Forms page editor) */
export const DESIGNER_EDITOR_LEFT_WIDTH_STORAGE_KEY =
    'designer-editor-left-width-v1';
export const DESIGNER_EDITOR_LEFT_WIDTH_MIN = 280;
export const DESIGNER_EDITOR_LEFT_WIDTH_MAX = 480;
export const DESIGNER_EDITOR_LEFT_WIDTH_DEFAULT = 320;

export const clampDesignerEditorLeftWidth = (width, minWidth, maxWidth) => {
    const min = minWidth || DESIGNER_EDITOR_LEFT_WIDTH_MIN;
    const max = maxWidth || DESIGNER_EDITOR_LEFT_WIDTH_MAX;
    return Math.min(max, Math.max(min, width));
};

export const getStoredDesignerEditorLeftWidth = (minWidth, maxWidth) => {
    try {
        const stored = Number(
            localStorage.getItem(DESIGNER_EDITOR_LEFT_WIDTH_STORAGE_KEY)
        );
        if (Number.isFinite(stored)) {
            return clampDesignerEditorLeftWidth(stored, minWidth, maxWidth);
        }
    } catch (e) {
        // ignore
    }
    return clampDesignerEditorLeftWidth(
        DESIGNER_EDITOR_LEFT_WIDTH_DEFAULT,
        minWidth,
        maxWidth
    );
};

// Export commonStyles for use in other components
export { commonStyles };
